// =============================================================================
// Lazy Alpine Initialization for below-fold sections
// MUST run first — theme.js is defer-loaded before vendors.js (Alpine), so
// x-ignore must be stamped onto below-fold sections before Alpine walks the DOM.
// Moving this to the top of the file maximises the time gap between marking and
// Alpine's startup walk, eliminating any risk of a race condition.
// =============================================================================
(function setupAlpineLazyInit() {
  if (typeof Shopify !== 'undefined' && Shopify.designMode) return;

  let viewportH = window.innerHeight;

  // Mark all Alpine sections that start completely below the initial viewport.
  // Batch pattern: Pass 1 collects all rects (single reflow), Pass 2 writes
  // attributes (single style invalidation) — avoids N reflows before LCP.
  let lazyToMark = [];
  document.querySelectorAll('.shopify-section').forEach(function(section) {
    if (!section.querySelector('[x-data],[x-show],[x-bind],[x-on],[x-intersect],[x-transition],[data-fade-on-intersect],[x-cloak]')) return;
    let rect = section.getBoundingClientRect(); // Pass 1: read only
    if (rect.top >= viewportH) lazyToMark.push(section);
  });
  lazyToMark.forEach(function(section) {        // Pass 2: write only
    section.setAttribute('x-ignore', '');
    section.setAttribute('data-alpine-lazy', '');
  });

  // After Alpine finishes above-fold sections, lazily init the rest on scroll
  document.addEventListener('alpine:initialized', function() {
    let lazyEls = document.querySelectorAll('[data-alpine-lazy]');
    if (!lazyEls.length) return;

    let sectionObserver = new IntersectionObserver(function(entries, obs) {
      entries.forEach(function(entry) {
        if (!entry.isIntersecting) return;
        let el = entry.target;
        obs.unobserve(el);
        el.removeAttribute('x-ignore');
        // CRITICAL: Alpine sets el._x_ignore as a JS *property* (not just the
        // HTML attribute) during its initial startup walk. Removing the attribute
        // alone is not enough — the property must also be deleted, otherwise:
        //   1. initTree's walk skips ALL children  (r._x_ignore && skip())
        //   2. Directive handlers bail out early    (e._x_ignore || return)
        //   → x-data scope never created, x-intersect / $store.* never run.
        delete el._x_ignore;
        // Add a root x-data scope so ALL Alpine magic ($store.*, $el, $refs)
        // works on any descendant directive without individual x-data per element.
        if (!el.hasAttribute('x-data')) el.setAttribute('x-data', '{}');
        Alpine.initTree(el);
        // Register any new data-fade-on-intersect elements revealed by Alpine
        if (window._fadeObserver) window._fadeObserver.observe(el);
      });
    }, { rootMargin: '300px 0px' }); // init 300px before visible to avoid pop-in

    lazyEls.forEach(function(el) { sectionObserver.observe(el); });
  });
})();

const installMediaQueryWatcher = (mediaQuery, changedCallback) => {
  const mq = window.matchMedia(mediaQuery);
  mq.addEventListener("change", (e) => changedCallback(e.matches));
  changedCallback(mq.matches);
};

const deferScriptLoad = (name, src, onload, requestVisualChange = false) => {
  ((events) => {
    const loadScript = () => {
      events.forEach((type) => window.removeEventListener(type, loadScript));
      clearTimeout(autoloadScript);

      const initScript = () => {
        const script = document.createElement("script");
        script.setAttribute("src", src);
        script.setAttribute("defer", "");
        script.onload = () => {
          window.Maximize.loadedScript.push(name);
          document.dispatchEvent(new CustomEvent(name + " loaded"));
          onload();
        };

        document.head.appendChild(script);
      };

      if (requestVisualChange) {
        if (window.requestIdleCallback) {
          requestIdleCallback(initScript);
        } else {
          requestAnimationFrame(initScript);
        }
      } else {
        initScript();
      }
    };

    let autoloadScript;
    if (Shopify.designMode) {
      loadScript();
    } else {
      const wait = window.innerWidth >= MIN_DEVICE_WIDTH.tablet ? 2000 : 5000;
      events.forEach((type) => window.addEventListener(type, loadScript, {once: true, passive: true}));
      autoloadScript = setTimeout(() => {
        loadScript();
      }, wait);
    }
  })(["touchstart", "mouseover", "wheel", "scroll", "keydown"]);
};

const getSectionInnerHTML = (html, selector = ".shopify-section") => {
  return new DOMParser().parseFromString(html, "text/html").querySelector(selector)?.innerHTML;
};

const xParseJSON = (jsonString) => {
  jsonString = String.raw`${jsonString}`;
  jsonString = jsonString.replaceAll("\\", "\\\\").replaceAll('\\"', '"');

  return JSON.parse(jsonString);
};

requestAnimationFrame(() => {
  document.addEventListener("alpine:init", () => {
    Alpine.store("xDarkMode", {
      isToggling: false,
      toggleThemeMode() {
        if (this.isToggling) return;
        this.isToggling = true;

        requestAnimationFrame(() => {
          document.documentElement.classList.add("show-icon-toggle-loading");
          requestAnimationFrame(() => {
            document.documentElement.classList.add("disable-transition-effect");

            const isDark = document.documentElement.classList.contains("dark")
            localStorage.maximize_theme = isDark ? 0 : 1;
            document.documentElement.classList.toggle("dark", !isDark);

            document.documentElement.classList.remove("show-icon-toggle-loading");
            requestAnimationFrame(() => {
              document.documentElement.classList.remove("disable-transition-effect");
              this.isToggling = false;
            })
          })
        })
      },
    });

    Alpine.store("xHelper", {
      countdown(configs, callback) {
        let endDate = new Date(configs.end_year, configs.end_month - 1, configs.end_day, configs.end_hour, configs.end_minute);
        const endTime = endDate.getTime() + (-1 * configs.timezone * 60 - endDate.getTimezoneOffset()) * 60 * 1000;

        let startTime;
        if (configs.start_year) {
          let startDate = new Date(configs.start_year, configs.start_month - 1, configs.start_day, configs.start_hour, configs.start_minute);
          startTime = startDate.getTime() + (-1 * configs.timezone * 60 - startDate.getTimezoneOffset()) * 60 * 1000;
        } else {
          startTime = new Date().getTime();
        }

        let x = setInterval(function () {
          let now = new Date().getTime();
          let distance = endTime - now;

          if (distance < 0 || startTime > now) {
            callback(false, 0, 0, 0, 0);
            clearInterval(x);
          } else {
            let days = Math.floor(distance / (1000 * 60 * 60 * 24));
            let hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
            let minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
            let seconds = Math.floor((distance % (1000 * 60)) / 1000);
            minutes = minutes < 10 ? "0" + minutes : minutes;
            seconds = seconds < 10 ? "0" + seconds : seconds;

            callback(true, seconds, minutes, hours, days);
          }
        }, 1000);
      },
      countdownByTime(startTime = Date.now(), endTime, callback) {
        const x = setInterval(() => {
        const now = Date.now();
        const distance = endTime - now;

        if (distance < 0 || startTime > now) {
          callback(false, 0, 0, 0, 0);
          clearInterval(x);
          return;
        }

        const days = Math.floor(distance / MS_PER_DAY);
        const hours = Math.floor(
          (distance % MS_PER_DAY) / MS_PER_HOUR
        );
        let minutes = Math.floor(
          (distance % MS_PER_HOUR) / MS_PER_MINUTE
        );
        let seconds = Math.floor(
          (distance % MS_PER_MINUTE) / MS_PER_SECOND
        );

        minutes = minutes < 10 ? "0" + minutes : minutes;
        seconds = seconds < 10 ? "0" + seconds : seconds;

          callback(true, seconds, minutes, hours, days);
        }, 1000);
      },
      canShow(configs) {
        let endDate = new Date(configs.end_year, configs.end_month - 1, configs.end_day, configs.end_hour, configs.end_minute);
        const endTime = endDate.getTime() + (-1 * configs.timezone * 60 - endDate.getTimezoneOffset()) * 60 * 1000;

        let startTime;
        if (configs.start_year) {
          let startDate = new Date(configs.start_year, configs.start_month - 1, configs.start_day, configs.start_hour, configs.start_minute);
          startTime = startDate.getTime() + (-1 * configs.timezone * 60 - startDate.getTimezoneOffset()) * 60 * 1000;
        } else {
          startTime = new Date().getTime();
        }
        let now = new Date().getTime();
        let distance = endTime - now;
        if (distance < 0 || startTime > now) {
          return false;
        }
        return true;
      },
      handleTime(configs) {
        let endDate = new Date(configs.end_year, configs.end_month - 1, configs.end_day, configs.end_hour, configs.end_minute);
        const endTime = endDate.getTime() + (-1 * configs.timezone * 60 - endDate.getTimezoneOffset()) * 60 * 1000;

        let startTime;
        if (configs.start_year) {
          let startDate = new Date(configs.start_year, configs.start_month - 1, configs.start_day, configs.start_hour, configs.start_minute);
          startTime = startDate.getTime() + (-1 * configs.timezone * 60 - startDate.getTimezoneOffset()) * 60 * 1000;
        } else {
          startTime = new Date().getTime();
        }
        let now = new Date().getTime();
        let distance = endTime - now;
        return {"startTime": startTime, "endTime": endTime, "now": now, "distance": distance};
      },
      setPosition(destinationId, elementId, needRemove = false) {
        let destinationElem = document.querySelector(destinationId);
        let movedElem = document.querySelector(elementId);
        if (destinationElem && movedElem) {
          destinationElem.innerHTML = movedElem.innerHTML;

          if (needRemove) {
            movedElem.remove();
          }

          // Re-observe fade elements. The source elements in #side-bar-template
          // were observed by MutationObserver while the div was hidden, so they
          // already carry data-fade-observed="1" serialised into their HTML.
          // innerHTML copy preserves that attribute, causing observeAll() to skip
          // them. Strip the stale guard first so the IntersectionObserver can bind.
          if (window._fadeObserver) {
            destinationElem.querySelectorAll('[data-fade-on-intersect]').forEach(function(el) {
              el.removeAttribute('data-fade-observed');
            });
            window._fadeObserver.observe(destinationElem);
          }
          const resizeEvent = new CustomEvent('resize');
          window.dispatchEvent(resizeEvent);
        }
      },
      dragToScroll(el, checkCanDrag = false) {
        const container = el;
        let isDragging = false;
        let startX;
        let scrollLeft;
        let canDrag = true;
        if (container) {
          container.addEventListener('mousedown', (e) => {
            isDragging = true;
            startX = e.pageX - container.offsetLeft;
            scrollLeft = container.scrollLeft;
          });

          container.addEventListener('mousemove', (e) => {
            if (!isDragging || !canDrag) return;
            e.preventDefault();
            const x = e.pageX - container.offsetLeft;
            const walk = x - startX;
            container.scrollLeft = scrollLeft - walk;
            requestAnimationFrame(() => {
              if (Math.abs(walk) > THRESHOLD.startScroll) {
                container.dataset.dragStartedScroll = "true";
              }
            });
          });

          container.addEventListener('mouseup', () => {
            if (!canDrag) return;
            isDragging = false;
            requestAnimationFrame(() => {
              container.removeAttribute("data-drag-started-scroll");
            })
          });

          container.addEventListener('mouseleave', () => {
            if (isDragging) {
              isDragging = false;
            }
          });

          if (checkCanDrag) {
            const updateCursorContainer = () => {
              if (container.scrollWidth > container.clientWidth) {
                container.classList.add('cursor-grab', 'select-none');
                canDrag = true;
              } else {
                container.classList.remove('cursor-grab', 'select-none');
                canDrag = false;
              }
            }

            updateCursorContainer();

            window.addEventListener('resize', debounce(() => {
              updateCursorContainer();
            }, TIMEOUT.resize));
          }
        }
      }
    });

    Alpine.data("xScrollNav", () => ({
      isRtl: window.Maximize.rtl,
      linkList: "",
      show: false,
      isAtStart: true,
      isAtEnd: false,
      navContentEl: null,
      navContentWidth: 0,
      navButtonWidth: 0,
      activeIndex: 0,
      __debounce(func, wait) {
        let timeout;
        return function (...args) {
          clearTimeout(timeout);
          timeout = setTimeout(() => func.apply(this, args), wait);
        };
      },
      init() {
        this.linkList = this.$el.querySelector(".link-list");
        this.linkList.addEventListener("scroll", () => {
          this.__debounce(this.checkScroll(), 20);
        })
        this.navContentEl = this.linkList.closest(".nav-content");
        this.navContentWidth = this.navContentEl.offsetWidth;

        const navButtons = this.$el.getElementsByClassName('scroll-nav');
        Alpine.nextTick(() => {
          const updateNavButtonWidth = () => {
            this.navButtonWidth = 0;
            Array.from(navButtons).forEach((button) => {
              if (this.navButtonWidth == 0) {
                this.navButtonWidth = button.getBoundingClientRect().width;
              }
            })
          }

          installMediaQueryWatcher(`(min-width: ${MIN_DEVICE_WIDTH.tablet}px)`, updateNavButtonWidth);

        })

        this.checkNavIsOverflow();

        // Re-check when element scrolls into view (handles off-screen init where offsetWidth = 0)
        let _self = this;
        let _visObs = new IntersectionObserver(function(entries) {
          entries.forEach(function(entry) {
            if (entry.isIntersecting) {
              _self.checkNavIsOverflow();
              _visObs.unobserve(entry.target);
            }
          });
        });
        _visObs.observe(this.$el);

        window.addEventListener("resize", () => {
          this.__debounce(this.checkNavIsOverflow(), 300);
          this.__debounce(this.checkScroll(), 20);
        });
      },
      checkNavIsOverflow() {
        this.navContentWidth = this.navContentEl.offsetWidth;
        if (this.linkList.scrollWidth > this.navContentWidth) {
          this.show = true;
        } else {
          this.show = false;
        }
      },
      scrollToLeft() {
        const multiplier = (window.innerWidth < MIN_DEVICE_WIDTH.tablet) ? 0.5 : 0.3
        this.linkList.scroll({
          top: 0,
          left: this.linkList.scrollLeft - (this.isRtl ? -(this.navContentWidth * multiplier) : (this.navContentWidth * multiplier)),
          behavior: "smooth",
        });
        this.checkScroll();
      },
      scrollToRight() {
        const multiplier = (window.innerWidth < MIN_DEVICE_WIDTH.tablet) ? 0.5 : 0.3
        this.linkList.scroll({
          top: 0,
          left: this.linkList.scrollLeft + (this.isRtl ? -(this.navContentWidth * multiplier) : (this.navContentWidth * multiplier)),
          behavior: "smooth",
        });
        this.checkScroll();
      },
      checkScroll() {
        //Delay to wait for scroll animation
        setTimeout(() => {
          const {scrollWidth, offsetWidth, scrollLeft} = this.linkList;
          this.isScrollable = scrollWidth > offsetWidth;

          const endOffset = scrollWidth - offsetWidth;

          if (this.isRtl) {
            this.isAtStart = Math.abs(scrollLeft) <= 1;
            this.isAtEnd = Math.abs(scrollLeft) >= endOffset - 2;
          } else {
            this.isAtStart = scrollLeft <= 1;
            this.isAtEnd = scrollLeft + offsetWidth >= scrollWidth - 2;
          }
        }, 300);
      },
      scrollToActiveItem(activeItem, hasEdgeGap) {
        const activeChild = activeItem || this.linkList.querySelector('.block-text-button__wrapper.is-active');
        if (!activeChild) return;

        const childRect = activeChild.getBoundingClientRect();
        const linkListRect = this.linkList.getBoundingClientRect();

        let offsetLeft = this.linkList.scrollLeft;
        if (this.isRtl) {
          offsetLeft += (childRect.right - linkListRect.right);
        } else {
          offsetLeft += (childRect.left - linkListRect.left);
        }

        if (hasEdgeGap && this.navButtonWidth) {
          offsetLeft += (this.navButtonWidth * 2) * (this.isRtl ? 1 : -1);
        }

        this.linkList.scroll({
          left: offsetLeft,
          behavior: 'smooth'
        });
        this.checkScroll();
      },
     
    }));

    Alpine.store("xMaximizePopup", {
      isOpen: false,
      handleOpen: function (activeElement, forceOverflowHidden = false) {
        this.isOpen = true;
        if (forceOverflowHidden || window.Maximize.isShowPopupOverlay) document.body.classList.add("overflow-y-hidden");
        if (activeElement) {
          setTimeout(() => {
            document.querySelector(`${activeElement}`)?.focus();
          }, 500)
        }
      },
      handleClose: function (forceOverflowHidden = false) {
        Alpine.store('xFocusElement').removeTrapFocus();
        setTimeout(() => {
          this.isOpen = false;
          if (forceOverflowHidden || window.Maximize.isShowPopupOverlay) document.body.classList.remove("overflow-y-hidden");
        }, 500);
      }
    })

    Alpine.store("xMaximizeDrawer", {
      isOpen: false,
      activeDrawerElement: false,
      durationTime: 0,
      drawerCloseElement: false,
      handleOpen: function (drawerContainer) {
        const drawerContainerEle = document.querySelector(drawerContainer);
        if (drawerContainerEle) {
          this.activeDrawerElement = drawerContainerEle;
          this.durationTime = DURATION.DRAWER_TRANSITION;
          drawerContainerEle.classList.add("is-open")
          this.isOpen = true;
          document.body.classList.add("overflow-y-hidden");
        }

        if (!this.drawerCloseElement) {
          this.drawerCloseElement = this.activeDrawerElement;
        }
      },
      handleClose: function (isCloseAllDrawer = true) {
        Alpine.store('xFocusElement').removeTrapFocus();
        this.isOpen = false;
        if (this.activeDrawerElement) {
          this.activeDrawerElement.classList.remove("is-open");
        }
        if (isCloseAllDrawer) {
          setTimeout(() => {
            if (this.activeDrawerElement ===  this.drawerCloseElement || !this.activeDrawerElement) {
              document.body.classList.remove("overflow-y-hidden");
              this.drawerCloseElement = false;
            } else {
              this.drawerCloseElement = this.activeDrawerElement;
            }
            this.activeDrawerElement = false;
          }, this.durationTime);
        } else {
          // Closing a nested drawer (e.g. size chart) without closing the parent (e.g. quick view).
          // Reset activeDrawerElement back to drawerCloseElement so the next handleClose(true)
          // correctly identifies it as the root drawer and removes overflow-y-hidden.
          this.activeDrawerElement = this.drawerCloseElement;
        }
      }
    })

    Alpine.store("xFocusElement", {
      focusableElements: ['button, [href], input:not([hidden]), select, textarea, [tabindex]:not([tabindex^="-"]), a:not([aria-disabled="true"])'],
      listeners: {},
      activeElement: null,
      isFocusable(el) {
        if (!el) return false;

        const style = getComputedStyle(el);

        if (!el.offsetParent || style.visibility === 'hidden' || style.display === 'none') {
          return false;
        }

        const tabindex = el.getAttribute('tabindex');
        if (tabindex !== null && parseInt(tabindex, 10) === -1) {
          return false;
        }

        return true;
      },
      trapFocus(containerSelector, elementFocusSelector) {
        const container = document.getElementById(containerSelector);
        const elementFocus = document.getElementById(elementFocusSelector);
        if (document.activeElement !== document.body && !container.contains(document.activeElement)) {
          this.activeElement = document.activeElement;
        }
        this.listeners = this.listeners || {};
        if (container) {
          const elements = Array.from(container.querySelectorAll(this.focusableElements));

          let firstEl = null;
          let lastEl = null;

          for (let i = 0; i <= elements.length - 1; i++) {
            if (this.isFocusable(elements[i])) {
              firstEl = elements[i];
              break;
            }
          }

          for (let i = elements.length - 1; i >= 0; i--) {
            if (this.isFocusable(elements[i])) {
              lastEl = elements[i];
              break;
            }
          }
          this.removeTrapFocus();

          this.listeners.focusin = (event) => {
            if (event.target !== container && event.target !== lastEl && event.target !== firstEl) {
              return;
            }
            document.addEventListener("keydown", this.listeners.keydown);
          };

          this.listeners.focusout = () => {
            document.removeEventListener("keydown", this.listeners.keydown);
          };

          this.listeners.keydown = (e) => {
            if (e.code.toUpperCase() !== "TAB") return;

            if (e.target === lastEl && !e.shiftKey) {
              e.preventDefault();
              firstEl.focus();
            }

            if ((e.target === firstEl || e.target == container) && e.shiftKey) {
              e.preventDefault();
              lastEl.focus();
            }
          };
          document.addEventListener("focusout", this.listeners.focusout);
          document.addEventListener("focusin", this.listeners.focusin);
          elementFocus?.focus();
        }
      },
      removeTrapFocus(elementToFocus = null) {
        document.removeEventListener("focusin", this.listeners.focusin);
        document.removeEventListener("focusout", this.listeners.focusout);
        document.removeEventListener("keydown", this.listeners.keydown);

        if (elementToFocus) {
          elementToFocus.focus();
        } else {
          this.activeElement?.focus();
        }
      }
    });

    Alpine.data('xTruncateText', () => ({
      truncateEl: "",
      truncateInnerEl: "",
      truncated: false,
      truncatable: false,
      label: "",
      expanded: false,
      load(truncateEl) {
        const truncateRect = truncateEl.getBoundingClientRect();
        truncateEl.style.setProperty("--truncate-height", `${truncateRect.height}px`);
      },
      setTruncate(element) {
        if (element.offsetHeight < element.scrollHeight || element.offsetWidth < element.scrollWidth) {
          this.truncated = true;
          this.truncatable = true;
          this.expanded = false;
        } else {
          this.truncated = false;
          this.truncatable = false
          this.expanded = true;
          ;
        }
      },
      open(el, newLabel) {
        const truncateEl = el.closest('.truncate-container').querySelector('.truncate-text');
        this.expanded = true;
        this.label = newLabel;
        if (truncateEl.classList.contains('truncate-expanded')) {
          this.truncated = true;
        } else {
          const truncateInnerEl = truncateEl.querySelector('.truncate-inner');
          window.requestAnimationFrame(() => {
            const truncateInnerRect = truncateInnerEl.getBoundingClientRect();
            truncateEl.style.setProperty("--truncate-height-expanded", `${truncateInnerRect.height}px`);
            truncateEl.classList.add('truncate-expanded');
          });
          this.truncated = false;
        }
      },
      close(el, newLabel, isPopup = false) {
        this.label = newLabel;
        const truncateEl = el.closest('.truncate-container').querySelector('.truncate-text');
        const isInViewport = () => {
          const rect = truncateEl.getBoundingClientRect();
          return (rect.top >= 0 && rect.left >= 0 && rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) && rect.right <= (window.innerWidth || document.documentElement.clientWidth))
        }
        this.truncated = true;
        if (!isInViewport()) {
          if (!isPopup) {
            const scrollPosition = truncateEl.getBoundingClientRect().top + window.scrollY - 100;
            window.scrollTo({
              top: scrollPosition,
              behavior: 'smooth'
            });
          } else {
            const drawerContent = el.closest('.drawer__content');
            const scrollPosition = truncateEl.getBoundingClientRect().top + drawerContent.scrollTop - 100;
            drawerContent.scrollTo({
              top: scrollPosition,
              behavior: 'smooth'
            });
          }
          truncateEl.style.transition = 'none'
          setTimeout(() => {
            truncateEl.style.transition = ''
          }, 1000)
        }
        truncateEl.classList.remove('truncate-expanded');
        this.expanded = false;
      }
    }));
  });
});

// Shared quantity-rules helpers (Shopify B2B min/max/increment). Rule values are
// seeded from Liquid (variant.quantity_rule) and passed into the components; JS
// only owns the clamp/snap/validate math and message interpolation.
const quantityRuleUtils = {
  get strings() {
    return (window.Maximize && window.Maximize.quantityRuleStrings) || {};
  },
  // [min, max, increment] to seed a rule from a variant object, spread into setRule().
  // A missing variant means the shopper picked an option combination that does not
  // exist (no rule) → default. A SOLD-OUT variant still exists and carries a real
  // quantity_rule, so honor it (seed its min) rather than collapsing to 1 — only the
  // absence of a rule falls back to the default (increment 1).
  ruleArgsForVariant(variant) {
    const qr = variant && variant.quantity_rule;
    if (!qr) return [1, null, 1];
    return [qr.min, qr.max, qr.increment];
  },
  normalizeRule(min, max, increment) {
    if (!window.Maximize.isB2BCustomer) return {min: 1, max: null, increment: 1};
    let m = parseInt(min);
    let inc = parseInt(increment);
    if (!Number.isInteger(m) || m < 1) m = 1;
    if (!Number.isInteger(inc) || inc < 1) inc = 1;
    // Shopify semantics: the quantity must be a MULTIPLE of increment (not an
    // offset from min), so align min up / max down to the increment grid. All
    // grid math below anchors at min, which is grid-aligned from here on.
    m = Math.ceil(m / inc) * inc;
    let mx = parseInt(max);
    if (max === null || max === undefined || max === '' || max === 'null' || !Number.isInteger(mx)) {
      mx = null;
    }
    if (mx !== null) mx = Math.floor(mx / inc) * inc;
    if (mx !== null && mx < m) mx = m;
    return {min: m, max: mx, increment: inc};
  },
  roundToIncrement(value, rule) {
    const {min, max, increment} = rule;
    if (increment <= 1) {
      let v = value < min ? min : value;
      if (max !== null && v > max) v = max;
      return v;
    }
    let snapped = min + Math.round((value - min) / increment) * increment;
    if (snapped < min) snapped = min;
    if (max !== null && snapped > max) {
      snapped = min + Math.floor((max - min) / increment) * increment;
    }
    return snapped;
  },
  normalizeQuantity(value, rule) {
    let v = parseInt(value);
    if (!Number.isInteger(v)) v = rule.min;
    if (v < rule.min) return rule.min;
    return this.roundToIncrement(v, rule);
  },
  // Step the quantity by one increment in the given direction (+1 / -1). A valid
  // value moves a full increment; an off-grid value snaps to the nearest valid
  // value IN the pressed direction. The result is clamped to [min, max].
  step(value, rule, direction) {
    const v = parseInt(value);
    if (!Number.isInteger(v)) return rule.min;
    let next;
    if (this.isValid(v, rule)) {
      next = v + direction * rule.increment;
    } else {
      const offset = (v - rule.min) / rule.increment;
      next = rule.min + (direction > 0 ? Math.ceil(offset) : Math.floor(offset)) * rule.increment;
    }
    if (next < rule.min) next = rule.min;
    if (rule.max !== null && next > rule.max) next = rule.max;
    return next;
  },
  isValid(value, rule) {
    const v = parseInt(value);
    if (!Number.isInteger(v)) return false;
    if (v < rule.min) return false;
    if (rule.max !== null && v > rule.max) return false;
    return (v - rule.min) % rule.increment === 0;
  },
  buildError(value, rule) {
    const s = this.strings;
    const v = parseInt(value);
    if (!Number.isInteger(v) || v < rule.min) {
      return (s.min || '').replace('[min]', rule.min);
    }
    if (rule.max !== null && v > rule.max) {
      return (s.max || '').replace('[max]', rule.max);
    }
    const lower = rule.min + Math.floor((v - rule.min) / rule.increment) * rule.increment;
    let upper = lower + rule.increment;
    if (rule.max !== null && upper > rule.max) upper = rule.max;
    return (s.step || '').replace('[lower]', lower).replace('[upper]', upper);
  },
  // Leaving the field commits a valid quantity: snap onto the rule's grid, then say WHY the
  // number changed. Only the focus-stealing half of the old blur handler was harmful — it
  // re-focused the input so a click on any other button never landed on its target. The
  // message itself never was, and dropping it left the quantity silently rewritten with no
  // explanation. Same shape as the cart and bulk-order handlers (_addQtyError / _setQtyError):
  // correct the value, show the reason, auto-hide. Nothing here moves focus.
  normalizeOnLeave(component, el) {
    const rule = component.rule;
    if (this.isValid(el.value, rule)) {
      component.__emitQuantityError('');
      return;
    }
    const message = this.buildError(el.value, rule); // build from the typed value, then correct it
    const snapped = this.normalizeQuantity(el.value, rule);
    component.qty = snapped;
    if (el.value !== String(snapped)) el.value = snapped;
    this.__emitErrorTimed(component, message);
  },
  // Correcting qty makes it valid, so the component's own qty watcher emits '' during Alpine's
  // reactive flush — emit after $nextTick so the message survives that, then auto-hide it the
  // way the cart's _addQtyError(…, autoHideMs) does. A newer emit cancels the pending hide via
  // the clearTimeout in __emitQuantityError.
  __emitErrorTimed(component, message, autoHideMs = 3000) {
    component.$nextTick(() => {
      component.__emitQuantityError(message);
      component.__errorHideTimer = setTimeout(() => component.__emitQuantityError(''), autoHideMs);
    });
  },
  // Explicit commit (Enter, or pressing Add to cart): report why the quantity is not
  // orderable and leave the typed value alone so the shopper can see what they entered.
  // Returns whether the value was valid.
  validateNow(component, el) {
    const rule = component.rule;
    if (this.isValid(el.value, rule)) {
      component.__emitQuantityError('');
      return true;
    }
    component.__emitQuantityError(this.buildError(el.value, rule));
    return false;
  },
  // Unit price for a quantity, given Shopify's price breaks: the discounted tier whose minimum the
  // quantity actually reaches, or the variant's own price below the first tier. For prices that are
  // built client-side (the product bundle summary) — everywhere the server can pre-render the
  // readings, showPriceTier below is preferred so Liquid keeps ownership of money formatting.
  // `priceBreaks` is the shape emitted into JSON by the Liquid snippets: [{minimumQuantity, price}].
  priceForQuantity(basePrice, priceBreaks, qty) {
    if (!Array.isArray(priceBreaks) || !priceBreaks.length) return basePrice;
    const quantity = parseInt(qty);
    if (!Number.isInteger(quantity)) return basePrice;

    let price = basePrice;
    let bestMin = -1;
    priceBreaks.forEach((priceBreak) => {
      const min = parseInt(priceBreak && priceBreak.minimumQuantity);
      if (!Number.isInteger(min) || min > quantity) return;
      if (min > bestMin) {
        bestMin = min;
        price = priceBreak.price;
      }
    });
    return price;
  },
  // Volume pricing: price.liquid renders one price reading per tier — every one of them, with
  // the tier's own compare-at price, discount percentage and sale label already resolved by
  // Liquid. All this does is reveal the reading that matches `qty` (the highest tier whose
  // minimum quantity it reaches) and hide the rest, so no money formatting or discount maths
  // is duplicated in JS.
  // One group can hold SEVERAL renderings of the same price — the sticky bar renders three
  // (mobile, desktop, mobile-expanded) and CSS shows whichever fits the viewport. Each one
  // therefore needs its own visible tier: resolving a single winner across the whole group left
  // the other renderings with every tier hidden, and the price vanished at those breakpoints.
  showPriceTier(containers, qty) {
    if (!containers || !containers.length) return;
    const quantity = parseInt(qty);

    containers.forEach((container) => {
      const tiers = container.querySelectorAll('[data-price-tier]');
      if (!tiers.length) return;

      let active = null;
      if (Number.isInteger(quantity)) {
        let activeMin = -1;
        tiers.forEach((tier) => {
          const min = parseInt(tier.dataset.priceTier);
          if (!Number.isInteger(min) || min > quantity) return;
          if (min > activeMin) {
            activeMin = min;
            active = tier;
          }
        });
      }
      // No tier reached (a blank quantity, or a rule minimum sitting below the first tier): the
      // cheapest tier still describes the price better than showing nothing at all.
      if (!active) active = tiers[0];

      tiers.forEach((tier) => {
        tier.hidden = tier !== active;
      });
    });
  },
};

requestAnimationFrame(() => {
  document.addEventListener("alpine:init", () => {
    Alpine.data("xQuantitySelector", (sectionId, currentVariantId, productUrl, isQtyPriceBreak = false, min = 1, max = null, increment = 1, errorScope = "") => {
      const initialRule = quantityRuleUtils.normalizeRule(min, max, increment);
      return {
        qty: initialRule.min,
        min: initialRule.min,
        max: initialRule.max,
        increment: initialRule.increment,
        currentVariantId: currentVariantId,
        get rule() {
          return { min: this.min, max: this.max, increment: this.increment };
        },
        get atMin() {
          const q = parseInt(this.qty, 10);
          return Number.isInteger(q) && q <= this.min;
        },
        get atMax() {
          if (this.max === null) return false;
          const q = parseInt(this.qty, 10);
          return Number.isInteger(q) && q >= this.max;
        },
        setRule(m, mx, inc) {
          const r = quantityRuleUtils.normalizeRule(m, mx, inc);
          this.min = r.min;
          this.max = r.max;
          this.increment = r.increment;
        },
        // Alpine calls this automatically for every consumer, which is the point: the sticky bar
        // wires its own `x-init` expression and never calls handleInit(), so anything parked in
        // there — the qty watcher included — simply never ran for it, and its price sat on the base
        // tier no matter what the quantity was. Keep this to what EVERY quantity selector needs.
        init() {
          this.$watch("qty", (newValue) => {
            if (this.$refs.quantity_input) {
              this.$refs.quantity_input.value = newValue;
            }
            if (window.Maximize.isB2BCustomer && quantityRuleUtils.isValid(newValue, this.rule)) {
              this.__emitQuantityError("");
            }
            this.__syncPriceToQuantity(newValue);
          });

          this.__syncPriceToQuantity(this.qty);
        },
        handleInit() {
          const inputValue = this.$refs.quantity_input && this.$refs.quantity_input.value;
          const parsedInput = parseInt(inputValue);
          this.qty = quantityRuleUtils.isValid(parsedInput, this.rule) ? parsedInput : this.min;

          document.addEventListener(`${PRODUCT_EVENT.updatedVariant}${sectionId}`, (event) => {
            const { currentVariant } = event.detail;

            if (currentVariant && currentVariant.id) {
              this.currentVariantId = currentVariant.id;
            }

            if (window.Maximize.isB2BCustomer) {
              // An unavailable option combination has no purchasable variant, so it has no
              // quantity rule either: fall back to the default rule instead of leaving the
              // previous variant's min/increment in the box.
              this.setRule(...quantityRuleUtils.ruleArgsForVariant(currentVariant));
            }

            if (this.$refs.quantity_input) {
              this.$refs.quantity_input.value = this.min;
            }

            this.qty = this.min;
          });

          document.addEventListener(`${CART_EVENT.cartUpdate}`, () => {
            fetch(`${productUrl}?variant=${this.currentVariantId}&section_id=${sectionId}`)
              .then((response) => response.text())
              .then((responseText) => {
                const html = new DOMParser().parseFromString(responseText, "text/html");
                const oldElement = document.querySelector(`.section--${sectionId} .quantity-selector__quantity-in-cart[data-current-variant-id="${this.currentVariantId}"]`);
                const newElement = html.querySelector(`.section--${sectionId} .quantity-selector__quantity-in-cart[data-current-variant-id="${this.currentVariantId}"]`);

                if (oldElement && newElement) {
                  oldElement.innerHTML = newElement.innerHTML;
                }
              })
              .catch((e) => console.error(e));
          });

          if (isQtyPriceBreak) {
            // Picking a tier only sets the quantity — no scrolling. The shopper clicked a row in
            // the volume-pricing table and is looking at it; yanking the viewport to the quantity
            // box moves the thing they just clicked out from under them.
            document.addEventListener(`QUANTITY_EVENT.volumnPricingChange-${sectionId}`, (event) => {
              this.qty = quantityRuleUtils.normalizeQuantity(event.detail.quantity, this.rule);
            });
          }
        },
        // Reveal the price reading for the current quantity. Runs on every qty change and once
        // on init; a variant change re-renders the price server-side and then resets qty to the
        // new rule's minimum, which comes back through the watcher — so no extra hook is needed.
        //
        // Matched by price group, not by section: the sticky bar renders its own price inside the
        // same section and keeps its own quantity, so a section-wide lookup would have each
        // selector retargeting the other's price. The group key is the selector's error scope.
        //
        // Deliberately NOT gated on isQtyPriceBreak: that flag comes from the product-level
        // `quantity_price_breaks_configured?`, which is false when the breaks arrive from a B2B
        // catalog instead of the product itself. showPriceTier is a no-op when nothing rendered
        // tiers, so presence of the markup is the more reliable gate.
        __syncPriceToQuantity(quantity) {
          const group = errorScope || sectionId;
          quantityRuleUtils.showPriceTier(document.querySelectorAll(`[data-price-group="${group}"]`), quantity);
        },
        minus() {
          // aria-disabled only (the control stays focusable) — enforce the actual bound here so
          // a keyboard Enter can't slip past the pointer-events-none on the dimmed button.
          if (this.atMin) return;
          this.qty = quantityRuleUtils.step(this.qty, this.rule, -1);
        },
        plus() {
          if (this.atMax) return;
          this.qty = quantityRuleUtils.step(this.qty, this.rule, 1);
        },
        // Leaving the field snaps to a valid quantity without reporting anything; Enter (and
        // Add to cart, via product-form) reports. No-op for non-B2B: the default rule is
        // always valid and the error UI isn't rendered.
        normalizeOnLeave(el) {
          if (!window.Maximize.isB2BCustomer) return;
          quantityRuleUtils.normalizeOnLeave(this, el);
        },
        validateNow(el) {
          if (!window.Maximize.isB2BCustomer) return true;
          return quantityRuleUtils.validateNow(this, el);
        },
        __emitQuantityError(message) {
          clearTimeout(this.__errorHideTimer);
          document.dispatchEvent(new CustomEvent(`quantity-rule-error:${errorScope || sectionId}`, { detail: { message } }));
        },
      };
    });

    Alpine.data("xSection", (sectionId, container) => ({
      sectionId: sectionId,
      loading: true,
      show_more: true,
      firstLoadData() {
        let url = `${window.location.pathname}?section_id=${this.sectionId}`;
        fetch(url, {
          method: "GET",
        })
          .then((response) => response.text())
          .then(() => {
            this.loading = false;
          });
      },
    }));
  });
});

requestAnimationFrame(() => {
  document.addEventListener("alpine:init", () => {
    Alpine.data("xQuantitySelectorAtc", (productId, index, min = 1, max = null, increment = 1, cardId = '') => ({
      qty: 1,
      min: 1,
      max: null,
      increment: 1,
      productId: productId,
      index: index,
      // Drives the quantity-rule tooltip: an unavailable variant has no rule and no
      // price to break down, so the info icon is hidden rather than showing stale data.
      hasQuantityRuleInfo: true,

      get quantityKey() {
        return `${this.productId}-${this.index}`;
      },

      get rule() {
        return {min: this.min, max: this.max, increment: this.increment};
      },

      // Same bound state as xQuantitySelector.atMin/atMax — see the comment there.
      get atMin() {
        const q = parseInt(this.qty, 10);
        return Number.isInteger(q) && q <= this.min;
      },
      get atMax() {
        if (this.max === null) return false;
        const q = parseInt(this.qty, 10);
        return Number.isInteger(q) && q >= this.max;
      },

      setRule(m, mx, inc) {
        const r = quantityRuleUtils.normalizeRule(m, mx, inc);
        this.min = r.min;
        this.max = r.max;
        this.increment = r.increment;
      },

      init() {
        if (window.Maximize.isB2BCustomer) this.setRule(min, max, increment);

        const stored = Alpine.store('productQuantity').get(this.quantityKey);
        this.qty = quantityRuleUtils.isValid(stored, this.rule) ? stored : this.min;
        Alpine.store('productQuantity').set(this.quantityKey, this.qty);
        this.__syncPriceToQuantity(this.qty);

        this.$watch(
          () => Alpine.store('productQuantity').get(this.quantityKey),
          (newValue) => {
            if (newValue !== this.qty) {
              this.qty = newValue;
            }
          }
        );

        this.$watch('qty', (newValue) => {
          Alpine.store('productQuantity').set(this.quantityKey, newValue);
          this.updateWrapperDataset(newValue);
          this.__syncPriceToQuantity(newValue);
          if (window.Maximize.isB2BCustomer && quantityRuleUtils.isValid(newValue, this.rule)) {
            this.__emitQuantityError('');
          }
        });

        // B2B-only: re-seed the rule when the card swatch changes variant. Non-B2B
        // keeps the default rule, so skip the listener.
        if (cardId && window.Maximize.isB2BCustomer) {
          document.addEventListener(`${PRODUCT_EVENT.swatchChange}${cardId}`, (event) => {
            const detail = event.detail || {};
            // Reset unconditionally: picking an unavailable option leaves no rule to read,
            // and keeping the previous variant's increment made the box reject valid input.
            const qr = detail.quantity_rule;
            if (qr) {
              this.setRule(qr.min, qr.max, qr.increment);
              this.hasQuantityRuleInfo = true;
            } else {
              const variant = detail.currentVariant;
              this.setRule(...quantityRuleUtils.ruleArgsForVariant(variant));
              this.hasQuantityRuleInfo = !!variant && variant.available !== false;
            }
            this.qty = this.min;
          });
        }
      },

      updateWrapperDataset(newValue) {
        const wrapper = this.$el.closest('.product-card__wrapper');
        if (wrapper) {
          wrapper.dataset.quantity = newValue;
        }
      },

      // Reveal the volume-pricing tier matching the current quantity. The card's price is
      // rendered with data-price-group set to this card's id (e.g. the choose-option popup);
      // a no-op when nothing rendered tiers, so it is safe for cards without volume pricing.
      __syncPriceToQuantity(quantity) {
        if (!cardId) return;
        quantityRuleUtils.showPriceTier(document.querySelectorAll(`[data-price-group="${cardId}"]`), quantity);
      },

      minus() {
        // aria-disabled only (the control stays focusable) — enforce the actual bound here so
        // a keyboard Enter can't slip past the pointer-events-none on the dimmed button.
        if (this.atMin) return;
        this.qty = quantityRuleUtils.step(this.qty, this.rule, -1);
      },

      plus() {
        if (this.atMax) return;
        this.qty = quantityRuleUtils.step(this.qty, this.rule, 1);
      },

      // Leaving the field snaps to a valid quantity without reporting anything; Enter (and
      // Add to cart, via product-form) reports into the card's error_message element.
      // No-op for non-B2B: the default rule is always valid and the error UI isn't rendered.
      normalizeOnLeave(el) {
        if (!window.Maximize.isB2BCustomer) return;
        quantityRuleUtils.normalizeOnLeave(this, el);
      },
      validateNow(el) {
        if (!window.Maximize.isB2BCustomer) return true;
        return quantityRuleUtils.validateNow(this, el);
      },
      __emitQuantityError(message) {
        clearTimeout(this.__errorHideTimer);
        if (!cardId) return;
        document.dispatchEvent(new CustomEvent(`quantity-rule-error:${cardId}`, {detail: {message}}));
      }
    }));

    Alpine.store('productQuantity', {
      quantities: {},

      set(key, value) {
        this.quantities[key] = Math.max(1, parseInt(value) || 1);
      },

      get(key) {
        return this.quantities[key] || 1;
      }
    });
  });
});

requestAnimationFrame(() => {
  document.addEventListener("alpine:init", () => {
    Alpine.store("xSwiffy", {
      swiffySlider: null,
      load(el, configs) {
        this.swiffySlider = el;
        const swiffy = new SwiffySlider(el, configs);

        if (configs.handleArrow) {
          this.handleArrowButton(swiffy, el);
        }

        if (configs.hotspot) {
          this.handleHotspot(swiffy, configs);
        }

        if (configs.paginationBar) {
          this.handlePaginationBar(swiffy, configs);
        }

        if (configs.progressBar) {
          this.handleProgressBar(swiffy, configs);
        }
      },
      handleHotspot(swiffy, configs) {
        const hotspotRoot = document.getElementById(configs.hotspot);
        const hotspots = hotspotRoot.getElementsByClassName("x-hotspot");

        for (let i = 0; i < hotspots.length; i++) {
          hotspots[i].addEventListener("mouseenter", function (e) {
            swiffy.slideTo(i);
          });
        }

        this.swiffySlider.addEventListener("maximize:swiffy-slider:slide", function (e) {
          const {oldActiveSlideIndex, newActiveSlideIndex} = e.detail;

          hotspots[oldActiveSlideIndex].classList.remove("active-hotspot");
          hotspots[newActiveSlideIndex].classList.add("active-hotspot");
        });
      },
      handleProgressBar(swiffy, configs) {
        let bar = swiffy.sliderElement.querySelector(".swiffy-slide-progress-bar");

        swiffy.sliderElement.addEventListener("maximize:swiffy-slider:slide", function (e) {
          const {oldActiveSlideIndex, newActiveSlideIndex} = e.detail;
          let end = configs.progressBar;

          let rate = 100 * (newActiveSlideIndex / end);
          if (bar) {
            let rateBar = rate + Number(bar.style.width.replace("%", ""));
            let maxRate = 100 - Number(bar.style.width.replace("%", ""));
            if (rateBar > 100) {
              rate = maxRate;
            }
            if (document.querySelector("body").classList.contains("rtl")) {
              bar.style.marginRight = rate + "%";
            } else {
              bar.style.marginLeft = rate + "%";
            }
          }
        });
      },
      handleArrowButton(swiffy, el) {
        const nextButton = el.querySelector(".arrow-next");
        const prevButton = el.querySelector(".arrow-prev");

        if (nextButton) {
          nextButton.addEventListener("click", function (e) {
            swiffy.slide();
          });
        }
        if (prevButton) {
          prevButton.addEventListener("click", function (e) {
            swiffy.slide(false);
          });
        }
      },
      handlePaginationBar(swiffy, configs) {
        swiffy.sliderElement.addEventListener("maximize:swiffy-slider:slide", (event) => {
          const newActiveSlideIndex = Number(event.detail.newActiveSlideIndex);
          if (configs.paginationBar == "number_bar") {
            if (event.detail.el.querySelector(".current-active-index")) {
              event.detail.el.querySelector(".current-active-index").textContent = newActiveSlideIndex + 1;
            }
          } else if (configs.paginationBar == "image") {
            try {
              let imagePaginationList = event.detail.el.querySelectorAll(".pagination-active-image");
              let newActivePagination = newActiveSlideIndex < imagePaginationList.length - 1 ? newActiveSlideIndex + 1 : 0;
              imagePaginationList.forEach((imagePagination, index) => {
                imagePagination.classList.toggle("active", index === newActivePagination);
              });
            } catch (e) {
              console.error(e);
            }
          }
          this.handleLoadingProgress(event.detail.el, configs);
        });
      },
      handleLoadingProgress(el, configs){
        if (configs.paginationBar == "image") {
          // let paginationWrapper = el.querySelector(".image-pagination-wrapper");
          // if (!paginationWrapper) return;
          // paginationWrapper.classList.remove("active");
          // setTimeout(() => {
          //   paginationWrapper.classList.add("active");
          // }, 300);

          const overlay = el.querySelector('.pagination-image-ovelay');
          if (!overlay) return;
          overlay.classList.remove("active");
          overlay.restartTimeout = setTimeout(() => {
            overlay.classList.add("active");
          }, 300);
        } else {
          const loadingEl = el.querySelector(".slider-autoplay-loading");
          if (!configs.autoPlay || !loadingEl) return;

          loadingEl.classList.remove("active");
          loadingEl.restartTimeout = setTimeout(() => {
            loadingEl.classList.add("active");
          }, 300);
        }
      }
    });
    Alpine.store("xProductCard", {
      loadedChooseOptions: {},
      async loadChooseOptions(url, el, optionId, index, isShowQuantity = false) {
        const productCardWrapper = el.closest(".product-card__wrapper")
        if (!productCardWrapper) return;
        const showB2BPriceLabel = productCardWrapper.dataset.showB2bPriceLabel
        let listOptionValuesIdSelected = productCardWrapper.dataset.listOptionValuesIdSelected
          ? productCardWrapper.dataset.listOptionValuesIdSelected.split(",").map(v => v.trim()).filter(Boolean)
          : [];
        let urlProduct = `${url}?option_values=${listOptionValuesIdSelected.join(",")}&section_id=choose-option&page=${index}`;

        let destinationElm = productCardWrapper.querySelector(".choose-option");
        let loadingEl = productCardWrapper.querySelector(".icon-loading");

        const renderQuantity = () => {
          const quantityContainer = destinationElm.querySelector('#ChooseOptionQuantity');
          
          if (isShowQuantity) {
            if (quantityContainer) {
              quantityContainer.classList.remove('hidden');
            }
          } else {
            if (quantityContainer) {
              quantityContainer.remove();
            }
          }
        };

        const handleB2BPriceLabel = () => {
          if (showB2BPriceLabel === 'false') {
            const b2bPriceLabels = destinationElm.querySelectorAll('.price-b2b-label');
            if (b2bPriceLabels) {
              b2bPriceLabels.forEach(label => {
                label.classList.add('hidden');
              });
            }
          }
        };
        
        if (this.loadedChooseOptions[urlProduct]) {
          destinationElm.innerHTML = this.loadedChooseOptions[urlProduct];
          renderQuantity();
          handleB2BPriceLabel();
          setTimeout(() => {
            this.focusChooseOption(destinationElm);
          }, 500);
          return true;
        }

        try {
          if (loadingEl) {
            loadingEl.classList.remove("hidden");
          }
          const response = await fetch(urlProduct);
          const content = await response.text();

          const parser = new DOMParser();
          let parsedContent = parser.parseFromString(content, "text/html").getElementById("ChooseOptionContent").innerHTML;
          if (parsedContent) {
            if (destinationElm) {
              destinationElm.innerHTML = parsedContent;
              renderQuantity();
              handleB2BPriceLabel();
            }
            if (!Shopify.designMode) {
              this.loadedChooseOptions[urlProduct] = parsedContent;
            }
          }
          if (loadingEl) {
            loadingEl.classList.add("hidden");
          }
        } catch (error) {
          console.error(error);
        }
        this.focusChooseOption(destinationElm);
      },

      focusChooseOption(el) {
        const closeButton = el.querySelector(".btn-close-choose-option");
        if (closeButton) {
          closeButton.focus({ preventScroll: true });
        }
      },   
      handleCardHover(el) {
        const swiffy = new SwiffySlider(el, {});
        let cardImage = swiffy.sliderElement.querySelector(".slider-container");
        if (window.innerWidth >= MIN_DEVICE_WIDTH.desktop) {
          let mouseMoveRafId;
          const onMouseMove = (e) => {
            if (mouseMoveRafId) cancelAnimationFrame(mouseMoveRafId);
            mouseMoveRafId = requestAnimationFrame(() => {
              let left = e.offsetX;
              let width = cardImage.getBoundingClientRect().width;
              if (window.Maximize.rtl) {
                left = width - left;
              }
              let spacing = left / width;
              let index = Math.floor(spacing * swiffy.slides.length);
              swiffy.dispatchEventSlide(swiffy.activeSlideIndex);
              swiffy.updateActiveSlide(index);
            });
          };

          let mouseLeaveRafId;
          const onMouseLeave = () => {
            if (mouseLeaveRafId) cancelAnimationFrame(mouseLeaveRafId);
            mouseLeaveRafId = requestAnimationFrame(() => {
              swiffy.dispatchEventSlide(swiffy.activeSlideIndex);
              swiffy.updateActiveSlide(0);
            });
          };

          cardImage.addEventListener("mousemove", onMouseMove);
          cardImage.addEventListener("mouseleave", onMouseLeave);
        }
      }
    });
  });
});

// Custom Elements extracted to custom-elements.js

/**
 * Count product items in the innerHTML of #shopify-section-predictive-search.
 * Used to resolve shopify:search:update with a totalCount.
 * Products in predictive-search.liquid have id="predictive-search-option-product-N".
 * @param {string} sectionHtml  innerHTML of #shopify-section-predictive-search
 * @returns {number}
 */
function _predictiveProductCount(sectionHtml) {
  try {
    var parsed = new DOMParser().parseFromString(sectionHtml || '', 'text/html');
    return parsed.querySelectorAll('[id^="predictive-search-option-product-"]').length;
  } catch (_) {
    return 0;
  }
}

requestAnimationFrame(() => {
  document.addEventListener("alpine:init", () => {
/* ============================================================
   PLEINE BULL — Search Engine v13 (custom xModalSearch)
   ============================================================ */

/* ── CSS ── */
(function() {
  if (document.getElementById("pb-css")) return;
  const s = document.createElement("style");
  s.id = "pb-css";
  s.textContent = `
#search-result { width:100% !important; max-width:100% !important; box-sizing:border-box !important; }
.pb-wrap{--r:#E30613;--n:#1B2A4A;--n06:rgba(27,42,74,.06);--n10:rgba(27,42,74,.09);--n20:rgba(27,42,74,.15);--n40:rgba(27,42,74,.4);--r08:rgba(227,6,19,.07);background:#fff;font-family:inherit;display:block;width:100% !important;max-width:100% !important;min-width:0 !important;box-sizing:border-box !important;overflow-x:hidden !important;padding:0 !important;margin:0 !important;}
.pb-inner{width:100%;max-width:100%;box-sizing:border-box;padding:8px 12px 6px;overflow-x:hidden;}
@media(min-width:768px){.pb-inner{padding:14px 20px 8px}}
.pb-chips{display:flex;flex-wrap:nowrap;gap:5px;overflow-x:auto;-webkit-overflow-scrolling:touch;scrollbar-width:none;padding-bottom:2px}
.pb-chips::-webkit-scrollbar{display:none}
@media(min-width:768px){.pb-chips{flex-wrap:wrap;overflow-x:visible}}
.pb-chip{display:inline-flex;align-items:center;padding:5px 11px;border-radius:999px;font-size:11.5px;font-weight:600;background:var(--n06);color:var(--n);border:1.5px solid var(--n20);text-decoration:none;white-space:nowrap;flex-shrink:0;line-height:1.4;transition:background .14s,color .14s}
.pb-chip:hover,.pb-chip:active{background:var(--n);color:#fff;border-color:var(--n)}
.pb-shortcuts{padding-bottom:10px;border-bottom:1px solid var(--n10)}
.pb-products{margin:10px 0 0}
.pb-section-hd{display:flex;align-items:center;justify-content:space-between;margin-bottom:8px}
.pb-section-title{display:flex;align-items:center;gap:6px;font-size:13px;font-weight:700;color:var(--n)}
.pb-bar{display:inline-block;width:3px;height:14px;border-radius:3px;background:var(--r);flex-shrink:0}
.pb-count{font-size:10px;color:var(--n40);font-weight:500}
.pb-grid{display:grid !important;grid-template-columns:repeat(2,1fr) !important;gap:8px;width:100% !important;box-sizing:border-box !important;}
@media(min-width:540px){.pb-grid{grid-template-columns:repeat(3,1fr) !important}}
@media(min-width:1024px){.pb-grid{grid-template-columns:repeat(6,1fr) !important;gap:10px !important}}
.pb-c{background:#fff;border-radius:8px;overflow:hidden;display:flex;flex-direction:column;position:relative;transition:box-shadow .2s;min-width:0}
.pb-c:hover{box-shadow:0 4px 16px rgba(0,0,0,.09)}
.pb-c-link{text-decoration:none;color:inherit;display:flex;flex-direction:column;height:100%}
.pb-c-img-wrap{position:relative;width:100%;padding-bottom:100%;background:#f5f5f5;border-radius:8px;overflow:hidden}
.pb-c-img{position:absolute;top:0;left:0;width:100%;height:100%;object-fit:contain;padding:5px}
.pb-c-img-ph{position:absolute;top:0;left:0;width:100%;height:100%;background:linear-gradient(135deg,#eef0f3,#e2e4e8)}
.pb-c-badges{position:absolute;top:4px;left:4px;display:flex;flex-direction:column;gap:2px;z-index:2}
.pb-b{display:inline-flex;align-items:center;padding:2px 5px;border-radius:3px;font-size:8px;font-weight:800;line-height:1.5}
.pb-b-steel{background:var(--n);color:#fff}
.pb-b-soft{background:#1565c0;color:#fff}
.pb-c-atc{position:absolute;bottom:5px;right:5px;opacity:0;transition:opacity .18s;z-index:2}
.pb-c:hover .pb-c-atc{opacity:1}
@media(hover:none){.pb-c-atc{opacity:1}}
.pb-c-atc-btn{display:flex;align-items:center;justify-content:center;width:30px;height:30px;border-radius:50%;background:var(--n);color:#fff;border:none;cursor:pointer}
.pb-c-body{padding:5px 4px 7px;display:flex;flex-direction:column;gap:2px;flex:1;min-width:0}
.pb-c-vendor{font-size:8px;font-weight:700;letter-spacing:.07em;text-transform:uppercase;color:var(--n40);margin:0;line-height:1.2}
.pb-c-title{font-size:11px;font-weight:600;color:var(--n);line-height:1.3;margin:0;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden}
@media(min-width:768px){.pb-c-title{font-size:12px}}
.pb-c-price-wrap{margin-top:auto;padding-top:4px}
.pb-c-price{font-size:12px;font-weight:700;color:var(--n);display:block}
@media(min-width:768px){.pb-c-price{font-size:14px}}
.pb-c-price-sale{color:var(--r)}
.pb-c-price-was{font-size:9px;color:var(--n40);font-weight:400;text-decoration:line-through}
.pb-voir-tous{margin:12px 0 0;text-align:center}
.pb-btn-all{display:inline-flex;align-items:center;gap:7px;padding:9px 24px;border-radius:8px;background:var(--n);color:#fff;font-size:12.5px;font-weight:700;text-decoration:none;border:2px solid var(--n);transition:background .15s,color .15s;cursor:pointer}
.pb-btn-all:hover,.pb-btn-all:active{background:#fff;color:var(--n)}
.pb-nores{text-align:center;padding:24px 16px}
.pb-nores-icon{font-size:28px;margin-bottom:8px}
.pb-nores-title{font-size:14px;font-weight:700;color:var(--n);margin:0 0 4px}
.pb-nores-hint{font-size:12px;color:var(--n40);margin:0}
.pb-extra-section{margin-top:12px;padding-top:10px;border-top:1px solid var(--n10)}
.pb-col-scroll{display:flex;flex-wrap:wrap;gap:6px;margin-top:6px}
.pb-col-card{display:inline-flex;align-items:center;gap:5px;padding:6px 13px;border-radius:999px;font-size:11.5px;font-weight:600;color:var(--n);background:var(--n06);border:1.5px solid var(--n20);text-decoration:none;white-space:nowrap;flex-shrink:0;transition:background .14s,color .14s}
.pb-col-card:hover,.pb-col-card:active{background:var(--n);color:#fff}
.pb-col-arrow{flex-shrink:0;opacity:.4}
.pb-art-list{display:flex;flex-direction:column;gap:8px;margin-top:6px}
@media(min-width:768px){.pb-art-list{display:grid;grid-template-columns:repeat(2,1fr);gap:10px}}
.pb-art-card{display:flex;align-items:flex-start;gap:10px;text-decoration:none;color:inherit;padding:8px;border-radius:8px;border:1px solid var(--n10);transition:border-color .15s;box-sizing:border-box}
.pb-art-card:hover{border-color:var(--r);background:var(--r08)}
.pb-art-img-wrap{width:80px;flex-shrink:0}
.pb-art-img{width:100%;height:auto;border-radius:6px;display:block}
.pb-art-img-ph{width:80px;height:60px;border-radius:6px;background:#eef0f3;flex-shrink:0}
.pb-art-body{display:flex;flex-direction:column;gap:4px;min-width:0;flex:1}
.pb-art-date{font-size:9px;color:var(--n40);font-weight:600;text-transform:uppercase;letter-spacing:.06em}
.pb-art-title{font-size:13px;font-weight:700;color:var(--n);line-height:1.4;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden}
.pb-explore{border-top:1px solid var(--n10);margin-top:10px;padding-top:10px;padding-bottom:4px}
.pb-suggest{position:absolute;top:calc(100% + 3px);left:0;right:0;background:#fff;border:1.5px solid var(--n10);border-radius:8px;box-shadow:0 8px 24px rgba(27,42,74,.12);z-index:9999;overflow:hidden;animation:pb-in .13s ease}
@keyframes pb-in{from{opacity:0;transform:translateY(-4px)}to{opacity:1;transform:translateY(0)}}
.pb-suggest__item{display:flex;align-items:center;gap:9px;width:100%;padding:9px 14px;background:none;border:none;cursor:pointer;font-size:13px;color:var(--n);text-align:left;border-bottom:1px solid var(--n06);transition:background .1s;font-family:inherit}
.pb-suggest__item:last-child{border-bottom:none}
.pb-suggest__item:hover,.pb-suggest__item:active{background:var(--r08)}
.pb-suggest__icon{color:var(--n40);flex-shrink:0}
.pb-suggest__arrow{color:rgba(27,42,74,.25);margin-left:auto;flex-shrink:0}
.pb-filters-bar{display:flex;align-items:center;justify-content:space-between;gap:10px;margin-bottom:10px;flex-wrap:wrap}
.pb-filters-toggle{position:relative;display:inline-flex;align-items:center;gap:7px;padding:9px 16px;border-radius:8px;background:#fff;color:var(--n);border:1.5px solid var(--n20);font-size:13px;font-weight:700;cursor:pointer;transition:background .14s,color .14s}
.pb-filters-toggle:hover{background:var(--n06)}
.pb-filters-dot{position:absolute;top:-3px;right:-3px;width:9px;height:9px;border-radius:50%;background:var(--r)}
.pb-sort-select{padding:9px 14px;border-radius:8px;background:#fff;color:var(--n);border:1.5px solid var(--n20);font-size:13px;font-weight:600;cursor:pointer}
.pb-filters-panel{display:flex;flex-wrap:wrap;gap:22px;padding:16px;margin-bottom:14px;background:var(--n06);border:1px solid var(--n10);border-radius:10px}
.pb-filter-group{min-width:150px}
.pb-filter-label{font-size:11px;font-weight:800;text-transform:uppercase;letter-spacing:.06em;color:var(--n40);margin:0 0 8px}
.pb-filter-check{display:flex;align-items:center;gap:7px;font-size:13px;color:var(--n);padding:4px 0;cursor:pointer}
.pb-filter-check input{accent-color:var(--n);width:14px;height:14px;flex-shrink:0}
.pb-filter-count{color:var(--n40);font-size:11px}
.pb-filter-price{display:flex;align-items:center;gap:8px}
.pb-filter-price input{width:70px;padding:6px 8px;border-radius:6px;border:1.5px solid var(--n20);font-size:13px;color:var(--n)}
.pb-filters-reset{align-self:flex-end;padding:8px 14px;border-radius:8px;background:none;border:1.5px solid var(--r);color:var(--r);font-size:12.5px;font-weight:700;cursor:pointer;height:fit-content}
.pb-filters-reset:hover{background:var(--r08)}
`;
  document.head.appendChild(s);
})();

/* ── Bloquer submit formulaire recherche hors page search ── */
(function() {
  document.addEventListener("submit", function(e) {
    if (window.location.pathname.indexOf("/search") !== -1) return;
    var form = e.target;
    if (form && typeof form.getAttribute === "function") {
      var action = form.getAttribute("action") || "";
      if (action.indexOf("/search") !== -1) { e.preventDefault(); e.stopPropagation(); }
    }
  }, true);
})();

    Alpine.data("xModalSearch", (type, desktopMaximumResults, mobileMaximumResults, productTypeSelected) => ({
      t: null,
      isFirstLoading: true,
      result: "",
      query: "",
      cachedResults: {},
      openResults: false,
      isFocusedSearchForm: false,
      showSuggest: false,
      loading: false,
      suggestions: [],
      productSuggestions: [],
      productLoading: false,
      productQuery: "",
      productRequestId: 0,

      init() {
        this.isFocusedSearchForm = false;
        this.openResults = false;
        this.showSuggest = true;
        this.productSuggestions = [];
        this.productLoading = false;
      },

      _isSP() { return window.location.pathname.indexOf("/search") !== -1; },

      _sugg: [
        "Fléchettes Bull's Blackjack laiton doré 20g","Fléchettes Bull's Kim Huybrechts 21g","Fléchettes Bull's Shark Pro noir 90% tungstène","Fléchettes Bull's Tank 22g","Fléchettes Bull's Thriller noir laiton 21g","Fléchettes acier Bull's Bronzo True 23g","Fléchettes acier Bull's Dirk van Duijvenbode 22g","Fléchettes acier Bull's Eagle 3 22g","Fléchettes acier Bull's Eagle 3 24g","Fléchettes acier Bull's Lightning Orange 23g","Fléchettes acier Bull's Lightning Rose 24g","Fléchettes acier Bull's Lightning Rouge 23g","Fléchettes acier Bull's Mario laiton 21g","Fléchettes acier Bull's Max Hopp 24g","Fléchettes acier Bull's Mercurius Ava 19g","Fléchettes acier Bull's Smoke Red Ember 23g","Fléchettes acier Jack Daniel's Old No7 laiton","Fléchettes acier Mission Horus 97,5% tungstène","Fléchettes acier Mission Josh Rock Brass Rocky laiton","Fléchettes acier Mission Lava 95% tungstène","Fléchettes acier Mission Scorpion 90% tungstène","Fléchettes acier Mission Venom 90% tungstène","Fléchettes acier Unicorn Gary Anderson Phase 6 90% Tungstène 22g",
        "Fléchettes soft tip Bull's Flora Zina 18g","Fléchettes soft tip Bull's Lightning Rose 19g","Fléchettes soft tip Bull's Lightning Rouge 21g","Fléchettes soft tip Bull's Phantom Grip 18g","Fléchettes soft tip Bull's Phantom Grip 22g","Fléchettes soft tip Bull's Roulette 18g","Fléchettes soft tip Bull's Shark Pro 20g","Fléchettes soft tip Jack Daniel's Old No7","Fléchettes soft tip Mission Horus 97,5% Tungstène 23g","Fléchettes soft tip Mission Josh Rock Rocky","Fléchettes soft tip Mission Lava 95% tungstène","Fléchettes soft tip Mission Nightfall M1 Straight Ring","Fléchettes soft tip Mission Scorpion 90% tungstène","Fléchettes soft tip Mission Venom 90% tungstène","Pack 3 jeux fléchettes Unicorn Converta acier et soft tip 17g",
        "fléchettes steel tip","fléchettes soft tip","fléchettes Mission","fléchettes Bull's","fléchettes Unicorn","fléchettes Jack Daniel's","fléchettes tungstène","fléchettes laiton","fléchettes 90%","fléchettes 95%","fléchettes 97%","fléchettes 18g","fléchettes 19g","fléchettes 20g","fléchettes 21g","fléchettes 22g","fléchettes 23g","fléchettes 24g","fléchettes 25g","fléchettes 26g","fléchettes noires","fléchettes rouges","fléchettes bleues","fléchettes vertes","fléchettes dorées","fléchettes argentées","fléchettes roses","fléchettes oranges","fléchettes débutant","fléchettes compétition",
        "Cible Winmau Blade 6","Cible Winmau Blade 6 Dual Core","Cible Winmau Blade 6 Triple Core Carbon","Cible de fléchettes Bull's Advantage 7.01 Pro","Cible de fléchettes Bull's The Classic","Cible de fléchettes Shot Michael Smith Competition","Cible de fléchettes Shot Rogue en sisal","Cible d'entraînement fléchettes Bull's Advantage Gold","Cible entraînement Unicorn Contender Trainer","Cible entraînement Unicorn HD Trainer","Cible fléchettes Unicorn Eclipse Pro","Cible fléchettes Unicorn Eclipse Pro 2","Cible fléchettes Unicorn Eclipse Ultra","Cible fléchettes Unicorn Eclipse Ultra 2.0","Cible fléchettes Unicorn Striker","Cible sisal Designa","Cible sisal Mission Samurai 4",
        "Cible électronique GRANBOARD 132","Cible électronique soft tip Unicorn LED avec 2 jeux de fléchettes","Cible sisal Jack Daniel's avec cabinet","Cible sisal Unicorn Striker avec cabinet",
        "cible sisal","cible électronique","cible Unicorn","cible Designa","cible Granboard","cible Bull's","cible Winmau","cible connectée","cible led","cible Blade 6",
        "Ailettes Mission Lava 100 microns Standard No6","Ailettes Mission Solo Horus 100 microns Standard No2","Ailettes Mission Solo Komodo 100 microns Standard No2","Ailettes Mission Solo Kuro 100 microns Standard No2","Ailettes Mission Solo Scorpion 100 microns Standard No2","Ailettes Mission Solo Venom 100 microns Standard No2","Ailettes Unicorn Ultrafly 75 AR1 Ghost Gary Anderson","Ailettes fléchettes Bull's Fortis 150 No.2","Ailettes fléchettes Bull's Mezzo 100 No.2","Ailettes fléchettes Bull's Phantom No.6","Ailettes fléchettes Bull's Piano 75 No.2","Ailettes fléchettes Bull's Retro 100 No.2","Ailettes fléchettes Robson Plus No.2",
        "ailettes standard","ailettes slim","ailettes No2","ailettes No6","ailettes Mission","ailettes Bull's","ailettes Unicorn","ailettes Robson","ailettes noires","ailettes rouges","ailettes bleues","ailettes vertes","ailettes oranges",
        "Tiges Mission Alicross Nylon","Tiges Mission Atom13 Aluminium","Tiges Mission Griplock","Tiges Mission Titan Fox titane","Tiges Unicorn Gripper 4","Tiges Bull's Alabama Short",
        "tiges courtes","tiges moyennes","tiges longues","tiges aluminium","tiges nylon","tiges titane","tiges Mission","tiges Bull's","tiges Unicorn",
        "Ailettes et tiges Mission Force Vortex No2","Ailettes et tiges Mission Force Vortex No6","Tiges et ailettes Bull's Union Pro","Tiges et ailettes Shot",
        "Pointes acier Bull's 32 mm","Pointes acier Bull's 38 mm","Pointes acier Bull's 45 mm","Pointes acier Bull's Osiris Grip","Pointes acier Unicorn Standard","Pointes de conversion Bull's","Pointes de fléchettes Mission Caliburn Ripple","Pointes de fléchettes Mission Glide","Pointes de fléchettes Mission Lasered","Pointes de fléchettes Mission Titan Pro Conversion",
        "pointes acier","pointes acier courtes","pointes acier longues","pointes de conversion",
        "Embouts soft tip Unicorn Epic noirs","Pointes de fléchettes Designa","Pointes de fléchettes Mission Titan Pro","Pointes de fléchettes Unicorn Spare Soft Tip","Pointes soft tip Bull's EVO","Pointes soft tip Bull's Long Life Short","Pointes soft tip Bull's Startip Short","pointes plastiques","pointes soft tip",
        "Etui fléchettes Mission Country EVA","Etui fléchettes Mission Freedom","Étui fléchettes Unicorn Vanguard","Étui pour fléchettes Bull's Lica 12","Étui pour fléchettes Bull's Morph","étui fléchettes","étui rigide","étui souple",
        "Anneau de protection cible Mission Surround Pro Heavy Duty","Anneau de protection de cible Bull's","Anneau de protection murale cible Unicorn Striker","Anneau de protection de cible Winmau","surround","anneau de protection cible",
        "Tapis de fléchettes Bull's Advantage","Tapis de fléchettes Bull's Illusion","Tapis fléchettes Unicorn Ultra 2.0","Tapis pro zone precision Winmau","tapis fléchettes",
        "Mission Torus 100 Eclairage cible led","Mission Torus 120 degres Eclairage cible","Système d'éclairage pour cible Bull's Lumo","Système d'éclairage pour cible Bull's Termote","Éclairage cible Winmau Polaris","éclairage cible","led cible",
        "Support fléchettes Mission Acrylic Arc","Support mural pour cible Bull's Click Fix","Ligne de tir autocollante Bull's Oche","Oche laser Viper","Extracteur de pointes Trinidad Shark","Aiguiseur de pointes Mission",
        "Mission","Bull's","Unicorn","Designa","Granboard","Shot","Winmau","Jack Daniel's","Trinidad","Robson","Viper",
      ],

      _CAT: {
        steel:["Steel Tip"],soft:["Soft Tip"],flechette:["Steel Tip","Soft Tip"],
        cible:["Cibles Sisal","Cibles électroniques","Cabinet"],"cible electronique":["Cibles électroniques"],"cible sisal":["Cibles Sisal","Cabinet"],ailette:["Ailettes","Fusion Ailettes Tiges"],
        tige:["Tiges","Fusion Ailettes Tiges"],"pointe acier":["Pointes acier"],"pointe soft":["Pointes plastiques"],
        pointe:["Pointes acier","Pointes plastiques"],etui:["Etuis"],tapis:["Tapis"],surround:["Surround"],pack:["Pack"],eclairage:["Eclairage"],
      },

      _norm(str) { return (str||"").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g,"").replace(/['’]/g,"").replace(/[^a-z0-9 ]/g," ").replace(/\s+/g," ").trim(); },

      _colorMap: {"verte":"vert","vertes":"vert","rouge":"rouge","rouges":"rouge","bleue":"bleu","bleues":"bleu","noire":"noir","noires":"noir","rose":"rose","roses":"rose","jaune":"jaune","jaunes":"jaune","orange":"orange","oranges":"orange","doree":"dore","dorees":"dore","doré":"dore","dorée":"dore","dorées":"dore","dores":"dore","argentee":"argent","argentees":"argent","argentée":"argent","argentées":"argent","violette":"violet","violettes":"violet","blanche":"blanc","blanches":"blanc","grise":"gris","grises":"gris","bronze":"bronze","bronzee":"bronze","titane":"titane","titan":"titane","tungstene":"tungstene","tungsten":"tungstene","laiton":"laiton","brass":"laiton"},

      _cat(query) {
        const q = this._norm(query);
        if (/pointes? (acier|steel)/.test(q)) return "pointe acier";
        if (/pointes? (plastiques?|soft)/.test(q)) return "pointe soft";
        if (q.includes("ailette")||q.includes("flight")||q.includes("plume")) return "ailette";
        if (/(cibles?|board) (electronique|electro|led)/.test(q)||q.includes("electronique")) return "cible electronique";
        if (q.includes("sisal")) return "cible sisal";
        if (q.includes("soft tip")) return "soft";
        if (q.includes("steel tip")) return "steel";
        if (q.includes("etui")||q.includes("sacoche")||q.includes("trousse")) return "etui";
        if (q.includes("pointe")) return "pointe";
        if (/^fl/.test(q)||q.includes("flechette")||q.includes("dart")) return "flechette";
        if (q.includes("soft")&&!q.includes("flechette")) return "soft";
        if (q.includes("steel")&&!q.includes("flechette")) return "steel";
        if (q.includes("cible")||q.includes("board")||q.includes("sisal")||q.includes("dartboard")||q.includes("cabinet")||q.includes("armoire")) return "cible";
        if (/\bvol\b/.test(q)) return "ailette";
        if (q.includes("tige")||q.includes("shaft")) return "tige";
        if (q.includes("tapis")) return "tapis";
        if (q.includes("surround")||q.includes("anneau")) return "surround";
        if (q.includes("eclairage")||q.includes("led")) return "eclairage";
        if (q.includes("pack")||q.includes("kit")||q.includes("coffret")) return "pack";
        return null;
      },

      _normalizeQuery(query) { const q = this._norm(query); return q.split(" ").map(w => this._colorMap[w] || w).join(" "); },

      _fuzzy(query) {
        const q = this._norm(query);
        const set = new Set([query, q]);
        const fixes = {"fl":"flechette","fle":"flechette","flec":"flechette","flech":"flechette","fleche":"flechette","flechett":"flechette","flechete":"flechette","flechetes":"flechette","flechettes":"flechette","flchette":"flechette","flchettes":"flechette","flechtte":"flechette","dart":"flechette","darts":"flechette","ci":"cible","cib":"cible","cibl":"cible","cibles":"cible","dartboard":"cible","board":"cible","sisal":"cible sisal","armoire":"cabinet","armoires":"cabinet","ai":"ailette","ail":"ailette","aile":"ailette","ailtte":"ailette","ailetes":"ailette","ailettes":"ailette","fli":"flight","fligh":"flight","flights":"flight","ti":"tige","tig":"tige","tge":"tige","tiges":"tige","sha":"shaft","shaf":"shaft","shafts":"shaft","poi":"pointe","poin":"pointe","point":"pointe","pointes":"pointe","etu":"etui","etuit":"etui","etuis":"etui","sacoche":"etui","trousse":"etui","tap":"tapis","tapi":"tapis","sur":"surround","surr":"surround","anneau":"surround","bul":"bulls","bull":"bulls","buls":"bulls","unicor":"unicorn","mision":"mission","missoin":"mission","harows":"harrows","harrow":"harrows","designas":"designa","dezigna":"designa","granboar":"granboard","granbord":"granboard","winmaux":"winmau","winmo":"winmau"};
        const fixed = fixes[q]; if (fixed) set.add(fixed);
        const dedupe = q.replace(/(.)\1+/g,"$1");
        if (dedupe !== q) { set.add(dedupe); if (fixes[dedupe]) set.add(fixes[dedupe]); }
        if (q.length > 3) { const t=q.slice(0,-1); set.add(t); if (fixes[t]) set.add(fixes[t]); }
        const normalized = this._normalizeQuery(q);
        if (normalized !== q) set.add(normalized);
        const words = q.split(" ");
        const withoutColor = words.filter(w => !this._colorMap[w]).join(" ").trim();
        if (withoutColor && withoutColor !== q && withoutColor.length > 1) set.add(withoutColor);
        return [...set].filter(v => v.length >= 2).slice(0,6);
      },

      _updateSugg(q) {
        if (!q || q.length < 2) { this.suggestions = []; return; }
        const qn = this._norm(q);
        const qNorm = this._normalizeQuery(qn);
        const words = qn.split(" ").filter(w => w.length > 0);
        this.suggestions = this._sugg.filter(s => {
          const sn = this._norm(s);
          if (sn === qn || sn === qNorm) return false;
          const allWords = words.every(w => sn.includes(w));
          const lastWord = words[words.length - 1];
          const otherWords = words.slice(0, -1);
          const partialMatch = otherWords.every(w => sn.includes(w)) && sn.split(" ").some(sw => sw.startsWith(lastWord));
          return allWords || partialMatch || sn.includes(qNorm);
        }).slice(0,6);
      },

      _productImage(p) {
        const raw = p?.featured_image?.url || p?.image?.url || p?.image || "";
        if (!raw) return "";
        return raw + (raw.includes("?") ? "&" : "?") + "width=160";
      },

      _formatPrice(value) {
        const price = parseFloat(value || "0");
        if (!price) return "";
        return price.toFixed(2).replace(".", ",") + "\u00a0€";
      },

      _productUrl(p) {
        return p?.url || (p?.handle ? "/products/" + p.handle : "#");
      },

      async _loadProductSuggestions(query) {
        const q = (query || "").trim();
        clearTimeout(this.t);

        if (q.length < 2) {
          this.productSuggestions = [];
          this.productLoading = false;
          this.productQuery = "";
          return;
        }

        const requestId = ++this.productRequestId;
        this.productLoading = true;
        this.productQuery = q;

        this.t = setTimeout(async () => {
          const products = await this._fetchProducts(q);

          if (requestId !== this.productRequestId || this.query.trim() !== q) return;

          this.productSuggestions = products
            .map((product, index) => ({
              product,
              index,
              score: this._score(product, q)
            }))
            .sort((a, b) => (b.score - a.score) || (a.index - b.index))
            .slice(0, 5)
            .map(item => item.product);

          this.productLoading = false;
        }, 220);
      },

      _goSearch(query) {
        if (!query || query.length < 1) return;
        const normalized = this._normalizeQuery(query);
        const cat = this._cat(normalized);
        const types = cat ? this._CAT[cat] : null;
        let url = '/search?q=' + encodeURIComponent(normalized) + '&type=product';
        if (types) url += types.map(t => '&filter.p.product_type=' + encodeURIComponent(t)).join('');
        window.location.href = url;
      },

      async _fetch(q) {
        try {
          const r = await fetch(Shopify.routes.root + "search/suggest.json?q=" + encodeURIComponent(q) + "&resources[type]=product&resources[limit]=20");
          const d = await r.json();
          return d.resources?.results?.products || [];
        } catch(e) { return []; }
      },

      async _fetchProducts(query) {
        const cat = this._cat(query);
        const variants = this._fuzzy(query);
        const types = cat ? this._CAT[cat] : null;
        const reqs = variants.map(v => this._fetch(v));
        if (types) { types.forEach(ty => { reqs.push(this._fetch((variants[0]||query) + " product_type:" + ty)); }); }
        const volumeQueries = {
          flechette:["flechettes","flechettes steel tip","flechettes soft tip","flechettes bulls","flechettes unicorn","flechettes mission","flechettes harrows","flechettes shot","flechettes designa","flechettes tungstene","flechettes laiton"],
          cible:["cible sisal","cible electronique","cible flechettes","cible unicorn","cible designa","dartboard","cible bulls"],
          ailette:["ailettes flechettes","ailettes bulls","ailettes mission","ailettes unicorn","ailettes slim","ailettes standard"],
          tige:["tiges flechettes","tiges aluminium","tiges nylon","tiges bulls","tiges mission","shafts darts"],
          pointe:["pointes acier","pointes plastiques","pointes flechettes","tips darts"],
          etui:["etui flechettes","etui darts","sac flechettes"],
          tapis:["tapis flechettes","tapis dartboard","mat darts"],
        };
        if (cat && volumeQueries[cat]) { volumeQueries[cat].forEach(vq => reqs.push(this._fetch(vq))); }
        const seen = new Set(); const out = [];
        const merge = list => { for (const p of list) if (!seen.has(p.handle)) { seen.add(p.handle); out.push(p); } };
        (await Promise.all(reqs)).forEach(merge);
        if (types) {
          const filtered = out.filter(p => types.includes(p.type));
          if (filtered.length > 0) return filtered;
          return out.filter(p => !["Etuis","Entretien","Accessoires","Tapis","Eclairage","Surround"].includes(p.type));
        }
        return out;
      },

      _score(p, query) {
        const q = this._norm(query);
        const t = this._norm(p.title||"");
        let sc = 0;
        if (t.startsWith(q)) sc += 20;
        else if (t.includes(q)) sc += 10;
        else if (t.split(" ").some(w => w.startsWith(q))) sc += 5;
        if (t.includes("22g")||t.includes("23g")||t.includes("24g")) sc += 2;
        return sc;
      },

      async _fetchExtras(query) {
        const qn = this._norm(query);
        try {
          const [artR, colR] = await Promise.all([
            fetch(Shopify.routes.root + "search/suggest.json?q=" + encodeURIComponent(query) + "&resources[type]=article&resources[limit]=6").then(r => r.json()).catch(() => ({})),
            fetch(Shopify.routes.root + "search/suggest.json?q=" + encodeURIComponent(query) + "&resources[type]=collection&resources[limit]=10").then(r => r.json()).catch(() => ({}))
          ]);
          const cols = colR.resources?.results?.collections || [];
          cols.sort((a,b) => { const ta=this._norm(a.title||""),tb=this._norm(b.title||""); return (ta.startsWith(qn)?0:ta.includes(qn)?1:2)-(tb.startsWith(qn)?0:tb.includes(qn)?1:2); });
          return { collections: cols.slice(0,8), articles: (artR.resources?.results?.articles||[]).slice(0,6) };
        } catch(e) { return {collections:[],articles:[]}; }
      },

      _card(p) {
        const pr = parseFloat(p.price||"0");
        const price = pr > 0 ? pr.toFixed(2).replace(".",",")+"\u00a0€" : "";
        const cp = parseFloat(p.compare_at_price_min||"0");
        const was = cp > pr ? cp.toFixed(2).replace(".",",")+"\u00a0€" : null;
        const img = (p.featured_image?.url||p.image) ? (p.featured_image?.url||p.image)+"&width=400" : null;
        const tags = (p.tags||[]).map(t=>(t||"").toLowerCase());
        const steel = p.type==="Steel Tip"||tags.some(t=>t.includes("steel tip"));
        const soft = p.type==="Soft Tip"||tags.some(t=>t.includes("soft tip"));
        const url = p.url||"/products/"+p.handle;
        const vid = p.variants?.[0]?.id||0;
        return `<div class="pb-c"><a href="${url}" class="pb-c-link"><div class="pb-c-img-wrap">${img?`<img src="${img}" alt="${(p.title||"").replace(/"/g,"&quot;")}" loading="lazy" class="pb-c-img">`:`<div class="pb-c-img-ph"></div>`}<div class="pb-c-badges">${steel?`<span class="pb-b pb-b-steel">⚙ Steel</span>`:""}${soft?`<span class="pb-b pb-b-soft">● Soft</span>`:""}</div>${vid?`<div class="pb-c-atc"><button class="pb-c-atc-btn" onclick="event.preventDefault();event.stopPropagation();var f=document.createElement('form');f.method='POST';f.action='/cart/add';var i=document.createElement('input');i.type='hidden';i.name='id';i.value='${vid}';f.appendChild(i);document.body.appendChild(f);f.submit();" title="Ajouter au panier"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="14" height="14"><path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 01-8 0"/></svg></button></div>`:""}</div><div class="pb-c-body">${p.vendor?`<p class="pb-c-vendor">${p.vendor}</p>`:""}<h3 class="pb-c-title">${p.title||""}</h3><div class="pb-c-price-wrap"><span class="pb-c-price${was?" pb-c-price-sale":""}">${price}</span>${was?`<span class="pb-c-price-was">${was}</span>`:""}</div></div></a></div>`;
      },
      _cardCol(c) { return `<a href="${c.url}" class="pb-col-card"><span>${c.title||""}</span><svg class="pb-col-arrow" width="11" height="11" viewBox="0 0 12 12" fill="none"><path d="M2 6h8M7 3l3 3-3 3" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg></a>`; },
      _cardArt(a) {
        const img = a.image||null;
        const date = a.published_at ? new Date(a.published_at).toLocaleDateString("fr-FR",{day:"2-digit",month:"short"}) : "";
        return `<a href="${a.url}" class="pb-art-card"><div class="pb-art-img-wrap">${img?`<img src="${img}" alt="${(a.title||"").replace(/"/g,"&quot;")}" loading="lazy" class="pb-art-img">`:`<div class="pb-art-img-ph"></div>`}</div><div class="pb-art-body"><span class="pb-art-date">${date}</span><span class="pb-art-title">${a.title||""}</span></div></a>`;
      },

      _render(products, query, extras) {
        const qE = encodeURIComponent(query);
        const root = (Shopify.routes.root||"/").replace(/\/$/,"");
        const {collections=[],articles=[]} = extras||{};
        const chips = [["Fléchettes","fl%C3%A9chettes"],["Soft tip","soft%20tip"],["Steel tip","steel%20tip"],["Cibles","cible"],["Étuis","%C3%A9tui"],["Shafts","shaft"],["Flights","flight"]].map(([l,s])=>`<a href="${root}/search?q=${s}&type=product" class="pb-chip">${l}</a>`).join("");
        const explore = [["+ Rouge",`${qE}%20rouge`],["+ 22g",`${qE}%2022g`],["+ Soft",`${qE}%20soft%20tip`],["+ Steel",`${qE}%20steel%20tip`],["Bull's",`bulls%20${qE}`],["Mission",`mission%20${qE}`]].map(([l,q])=>`<a href="${root}/search?q=${q}&type=product" class="pb-chip">${l}</a>`).join("");
        const cat = this._cat(query);
        const filterParam = (cat==="flechette")?"&filter.p.product_type=Steel+Tip&filter.p.product_type=Soft+Tip":(cat==="cible")?"&filter.p.product_type=Cibles+Sisal&filter.p.product_type=Cibles+%C3%A9lectroniques":(cat==="ailette")?"&filter.p.product_type=Ailettes":(cat==="tige")?"&filter.p.product_type=Tiges":(cat==="pointe")?"&filter.p.product_type=Pointes+acier&filter.p.product_type=Pointes+plastiques":(cat==="etui")?"&filter.p.product_type=Etuis":"";
        const n = products.length;
        const voir = `<div class="pb-voir-tous"><a href="${root}/search?q=${qE}&type=product${filterParam}" class="pb-btn-all">Voir tous les résultats <svg width="13" height="13" viewBox="0 0 13 13" fill="none"><path d="M2 6.5h9M8 3l3.5 3.5L8 10" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"/></svg></a></div>`;
        const cards = n > 0 ? `<div class="pb-products"><div class="pb-section-hd"><span class="pb-section-title"><span class="pb-bar"></span>Produits</span><span class="pb-count">${n} résultat${n>1?"s":""}</span></div><div class="pb-grid">${products.map(p=>this._card(p)).join("")}</div>${voir}</div>` : `<div class="pb-nores"><div class="pb-nores-icon">🎯</div><p class="pb-nores-title">Aucun résultat pour "${query}"</p><p class="pb-nores-hint">Essaie une marque, un poids ou une couleur.</p></div>${voir}`;
        const colH = collections.length ? `<div class="pb-extra-section"><span class="pb-lbl">Collections</span><div class="pb-col-scroll">${collections.map(c=>this._cardCol(c)).join("")}</div></div>` : "";
        const artH = articles.length ? `<div class="pb-extra-section"><span class="pb-lbl">Articles du blog</span><div class="pb-art-list">${articles.map(a=>this._cardArt(a)).join("")}</div></div>` : "";
        return `<div class="pb-wrap"><div class="pb-inner"><div class="pb-shortcuts"><span class="pb-lbl">Accès rapide</span><div class="pb-chips">${chips}</div></div>${cards}${colH}${artH}<div class="pb-explore"><span class="pb-lbl">Explorer aussi</span><div class="pb-chips">${explore}</div></div></div></div>`;
      },

      async getSearchResult(query) { this._goSearch(query); },

      applySuggestion(s) {
        this.query = s;
        this.suggestions = [];
        this.productSuggestions = [];
        this.productLoading = false;
        this.showSuggest = false;
        if (this.$refs.input_search) { this.$refs.input_search.value = s; this.$refs.input_search.blur(); }
        this._goSearch(s);
      },

      open() {
        this.isFocusedSearchForm = true;
        this.showSuggest = true;
        setTimeout(() => { if (this.$refs.input_search) this.$refs.input_search.focus(); }, 50);
      },

      close() {
        this.isFocusedSearchForm = false;
        this.openResults = false;
        this.suggestions = [];
        this.productSuggestions = [];
        this.productLoading = false;
        this.productRequestId++;
      },

      handleChangeInput() {
        this.query = this.$el.value;
        this._updateSugg(this.query);
        this._loadProductSuggestions(this.query);
        if (this.query.length === 0) {
          this.showSuggest = true;
          this.suggestions = [];
          this.productSuggestions = [];
        }
      },

      focusForm() {
        this.isFocusedSearchForm = true;
        this.showSuggest = true;
        const input = this.$refs.input_search;
        const v = this.query || input?.value || "";
        if (v.length >= 2) {
          if (input) { input.value = v; input.select(); }
          this._updateSugg(v);
          this._loadProductSuggestions(v);
        } else {
          if (input) input.value = "";
          this.query = "";
          this.suggestions = [];
          this.productSuggestions = [];
          this.productLoading = false;
        }
      },

      handleKeyUpModal(event) {
        if (["Escape","Tab","Shift","Control","Alt","Meta","ArrowUp","ArrowDown"].includes(event.key)) {
          if (event.key === "Escape") {
            this.suggestions = [];
            this.productSuggestions = [];
            this.productLoading = false;
            this.productRequestId++;
          }
          return;
        }
        if (event.key === "Enter") {
          this.suggestions = [];
          if (this.$refs.input_search) this.$refs.input_search.blur();
          event.preventDefault();
          event.stopImmediatePropagation();
          clearTimeout(this.t);
          const q = this.query || event.target.value || "";
          if (q.length >= 1) this._goSearch(q);
          return;
        }
        this.query = event.target.value;
        this._updateSugg(this.query);
        this._loadProductSuggestions(this.query);
        if (this.query.length === 0) {
          this.showSuggest = true;
          this.suggestions = [];
          this.productSuggestions = [];
        }
      },

      setSearchBoxHeight() {
        Alpine.nextTick(() => {
          if (this.$refs.search_content) {
            const rect = this.$refs.search_content.getBoundingClientRect();
            if (rect.top > 0) this.$refs.search_content.style.setProperty("--input-search-to-top", `${rect.top}px`);
          }
        });
      },

      /* Stub pour compatibilité Vast (appelé par le nouveau header-search.liquid) */
      fetchRefinedSearch() {},
      setProductType() {},
      handleChangeProductType() {},
    }));

    Alpine.data("xSearchPageResults", (initialQuery) => ({
      query: initialQuery || "",
      loading: true,
      products: [],
      visibleCount: 24,
      pageSize: 24,
      showFilters: false,
      sortBy: "pertinence",
      filters: { vendors: [], types: [], colors: [], inStock: false, minPrice: "", maxPrice: "" },

      init() {
        this._runSearch();
      },

      _norm(str) { return (str||"").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g,"").replace(/['’]/g,"").replace(/[^a-z0-9 ]/g," ").replace(/\s+/g," ").trim(); },

      _colorMap: {"verte":"vert","vertes":"vert","rouge":"rouge","rouges":"rouge","bleue":"bleu","bleues":"bleu","noire":"noir","noires":"noir","rose":"rose","roses":"rose","jaune":"jaune","jaunes":"jaune","orange":"orange","oranges":"orange","doree":"dore","dorees":"dore","doré":"dore","dorée":"dore","dorées":"dore","dores":"dore","argentee":"argent","argentees":"argent","argentée":"argent","argentées":"argent","violette":"violet","violettes":"violet","blanche":"blanc","blanches":"blanc","grise":"gris","grises":"gris","bronze":"bronze","bronzee":"bronze","titane":"titane","titan":"titane","tungstene":"tungstene","tungsten":"tungstene","laiton":"laiton","brass":"laiton"},

      _CAT: {
        steel:["Steel Tip"],soft:["Soft Tip"],flechette:["Steel Tip","Soft Tip"],
        cible:["Cibles Sisal","Cibles électroniques","Cabinet"],"cible electronique":["Cibles électroniques"],"cible sisal":["Cibles Sisal","Cabinet"],ailette:["Ailettes","Fusion Ailettes Tiges"],
        tige:["Tiges","Fusion Ailettes Tiges"],"pointe acier":["Pointes acier"],"pointe soft":["Pointes plastiques"],
        pointe:["Pointes acier","Pointes plastiques"],etui:["Etuis"],tapis:["Tapis"],surround:["Surround"],pack:["Pack"],eclairage:["Eclairage"],
      },

      _normalizeQuery(query) { const q = this._norm(query); return q.split(" ").map(w => this._colorMap[w] || w).join(" "); },

      _cat(query) {
        const q = this._norm(query);
        if (/pointes? (acier|steel)/.test(q)) return "pointe acier";
        if (/pointes? (plastiques?|soft)/.test(q)) return "pointe soft";
        if (q.includes("ailette")||q.includes("flight")||q.includes("plume")) return "ailette";
        if (/(cibles?|board) (electronique|electro|led)/.test(q)||q.includes("electronique")) return "cible electronique";
        if (q.includes("sisal")) return "cible sisal";
        if (q.includes("soft tip")) return "soft";
        if (q.includes("steel tip")) return "steel";
        if (q.includes("etui")||q.includes("sacoche")||q.includes("trousse")) return "etui";
        if (q.includes("pointe")) return "pointe";
        if (/^fl/.test(q)||q.includes("flechette")||q.includes("dart")) return "flechette";
        if (q.includes("soft")&&!q.includes("flechette")) return "soft";
        if (q.includes("steel")&&!q.includes("flechette")) return "steel";
        if (q.includes("cible")||q.includes("board")||q.includes("sisal")||q.includes("dartboard")||q.includes("cabinet")||q.includes("armoire")) return "cible";
        if (/\bvol\b/.test(q)) return "ailette";
        if (q.includes("tige")||q.includes("shaft")) return "tige";
        if (q.includes("tapis")) return "tapis";
        if (q.includes("surround")||q.includes("anneau")) return "surround";
        if (q.includes("eclairage")||q.includes("led")) return "eclairage";
        if (q.includes("pack")||q.includes("kit")||q.includes("coffret")) return "pack";
        return null;
      },

      _fuzzy(query) {
        const q = this._norm(query);
        const set = new Set([query, q]);
        const fixes = {"fl":"flechette","fle":"flechette","flec":"flechette","flech":"flechette","fleche":"flechette","flechett":"flechette","flechete":"flechette","flechetes":"flechette","flechettes":"flechette","flchette":"flechette","flchettes":"flechette","flechtte":"flechette","dart":"flechette","darts":"flechette","ci":"cible","cib":"cible","cibl":"cible","cibles":"cible","dartboard":"cible","board":"cible","sisal":"cible sisal","armoire":"cabinet","armoires":"cabinet","ai":"ailette","ail":"ailette","aile":"ailette","ailtte":"ailette","ailetes":"ailette","ailettes":"ailette","fli":"flight","fligh":"flight","flights":"flight","ti":"tige","tig":"tige","tge":"tige","tiges":"tige","sha":"shaft","shaf":"shaft","shafts":"shaft","poi":"pointe","poin":"pointe","point":"pointe","pointes":"pointe","etu":"etui","etuit":"etui","etuis":"etui","sacoche":"etui","trousse":"etui","tap":"tapis","tapi":"tapis","sur":"surround","surr":"surround","anneau":"surround","bul":"bulls","bull":"bulls","buls":"bulls","unicor":"unicorn","mision":"mission","missoin":"mission","harows":"harrows","harrow":"harrows","designas":"designa","dezigna":"designa","granboar":"granboard","granbord":"granboard","winmaux":"winmau","winmo":"winmau"};
        const fixed = fixes[q]; if (fixed) set.add(fixed);
        const dedupe = q.replace(/(.)\1+/g,"$1");
        if (dedupe !== q) { set.add(dedupe); if (fixes[dedupe]) set.add(fixes[dedupe]); }
        if (q.length > 3) { const t=q.slice(0,-1); set.add(t); if (fixes[t]) set.add(fixes[t]); }
        const normalized = this._normalizeQuery(q);
        if (normalized !== q) set.add(normalized);
        const words = q.split(" ");
        const withoutColor = words.filter(w => !this._colorMap[w]).join(" ").trim();
        if (withoutColor && withoutColor !== q && withoutColor.length > 1) set.add(withoutColor);
        return [...set].filter(v => v.length >= 2).slice(0,6);
      },

      _MODIFIER_WORDS: ["vert","rouge","bleu","noir","rose","jaune","orange","dore","argent","violet","blanc","gris","bronze","titane","tungstene","laiton"],

      _extractModifiers(query) {
        const qn = this._normalizeQuery(query);
        const words = qn.split(" ").filter(Boolean);
        return words.filter(w => this._MODIFIER_WORDS.includes(w) || /^\d{2}g$/.test(w));
      },

      _TYPE_COLLECTIONS: {
        "Steel Tip": "flechettes-acier-steeltip",
        "Soft Tip": "flechettes-plastique-softtip",
        "Ailettes": "ailettes-flechettes",
        "Tiges": "tiges-shafts",
        "Fusion Ailettes Tiges": "fusion-ailettes-tiges",
        "Pointes acier": "pointes-acier-flechettes",
        "Pointes plastiques": "pointes-plastique-flechettes",
        "Etuis": "etuis-rangement-flechettes",
        "Cibles Sisal": "cibles-sisal",
        "Cibles électroniques": "cibles-electroniques",
        "Surround": "surrounds-protections-murales",
        "Eclairage": "eclairage-cible-flechettes",
        "Tapis": "tapis-flechettes-oches",
        "Entretien": "entretien-maintenance-flechettes",
        "Cabinet": "cabinet",
      },

      _COLOR_TAGS: ["noir","blanc","rouge","bleu","vert","jaune","orange","rose","violet","gris","dore","argent","bronze"],

      _normalizeProduct(p, rankIndex) {
        const variants = p.variants || [];
        const v0 = variants[0] || {};
        const prices = variants.map(v => parseFloat(v.price)).filter(n => !isNaN(n));
        const comparePrices = variants.map(v => parseFloat(v.compare_at_price)).filter(n => !isNaN(n));
        const tags = p.tags || [];
        return {
          handle: p.handle,
          title: p.title,
          vendor: p.vendor,
          type: (p.product_type || "").replace(/\s+/g, " ").trim(),
          tags: tags,
          url: "/products/" + p.handle,
          price: (prices.length ? Math.min(...prices) : parseFloat(v0.price) || 0).toString(),
          compare_at_price_min: (comparePrices.length ? Math.min(...comparePrices) : parseFloat(v0.compare_at_price) || 0).toString(),
          featured_image: (p.images && p.images[0]) ? { url: p.images[0].src } : null,
          variants: variants.map(v => ({ id: v.id })),
          available: variants.length ? variants.some(v => v.available !== false) : true,
          colors: tags.map(t => this._norm(t)).filter(t => this._COLOR_TAGS.includes(t)),
          // Position dans la collection triée manuellement par le Worker de ranking business
          // (stock, marge, ventes, reco_priority, joueur, marque...). Plus petit = mieux classé.
          rankIndex: typeof rankIndex === "number" ? rankIndex : 9999,
        };
      },

      async _fetchJsonPages(baseUrl, fields) {
        const fetchPage = async (page) => {
          try {
            const sep = baseUrl.includes("?") ? "&" : "?";
            const r = await fetch(baseUrl + sep + "limit=250&page=" + page + (fields ? "&fields=" + fields : ""));
            const d = await r.json();
            return d.products || [];
          } catch(e) { return []; }
        };
        let all = [];
        let page = 1;
        const MAX_PAGES = 4;
        while (page <= MAX_PAGES) {
          const batch = await fetchPage(page);
          all = all.concat(batch);
          if (batch.length < 250) break;
          page++;
        }
        return all;
      },

      async _loadCatalog() {
        const cacheKey = "pb_catalog_v4";
        try {
          const cached = sessionStorage.getItem(cacheKey);
          if (cached) {
            const parsed = JSON.parse(cached);
            if (Date.now() - parsed.ts < 60 * 60 * 1000 && Array.isArray(parsed.products)) { return parsed.products; }
          }
        } catch(e) {}

        const fields = "id,handle,title,vendor,product_type,tags,variants,images";
        const seen = new Set();
        let all = [];

        // 1) Collections typées, triées manuellement par le Worker de ranking business
        //    → on garde leur ordre tel quel (rankIndex = position dans la collection)
        const handles = Object.values(this._TYPE_COLLECTIONS);
        const collectionResults = await Promise.all(
          handles.map(h => this._fetchJsonPages("/collections/" + h + "/products.json?sort_by=manual", fields))
        );
        collectionResults.forEach(list => {
          list.forEach((p, idx) => {
            if (seen.has(p.handle)) return;
            seen.add(p.handle);
            all.push(this._normalizeProduct(p, idx));
          });
        });

        // 2) Filet de sécurité : catalogue complet, pour les produits absents des collections
        //    ci-dessus (type manquant/mal renseigné) ou pour les recherches par marque/modèle
        //    qui traversent plusieurs types. Pas de rankIndex fiable ici (classés en dernier).
        const fullCatalog = await this._fetchJsonPages("/products.json", fields);
        fullCatalog.forEach(p => {
          if (seen.has(p.handle)) return;
          seen.add(p.handle);
          all.push(this._normalizeProduct(p));
        });

        try { sessionStorage.setItem(cacheKey, JSON.stringify({ ts: Date.now(), products: all })); } catch(e) {}
        return all;
      },

      _hasWordPrefix(text, word) {
        return (text || "").split(" ").some(tok => tok.length > 0 && tok.startsWith(word));
      },

      _fuzzyStrict(query) {
        const q = this._norm(query);
        const set = new Set([query, q]);
        const fixes = {"fl":"flechette","fle":"flechette","flec":"flechette","flech":"flechette","fleche":"flechette","flechett":"flechette","flechete":"flechette","flechetes":"flechette","flechettes":"flechette","flchette":"flechette","flchettes":"flechette","flechtte":"flechette","dart":"flechette","darts":"flechette","ci":"cible","cib":"cible","cibl":"cible","cibles":"cible","dartboard":"cible","board":"cible","sisal":"cible sisal","armoire":"cabinet","armoires":"cabinet","ai":"ailette","ail":"ailette","aile":"ailette","ailtte":"ailette","ailetes":"ailette","ailettes":"ailette","fli":"flight","fligh":"flight","flights":"flight","ti":"tige","tig":"tige","tge":"tige","tiges":"tige","sha":"shaft","shaf":"shaft","shafts":"shaft","poi":"pointe","poin":"pointe","point":"pointe","pointes":"pointe","etu":"etui","etuit":"etui","etuis":"etui","sacoche":"etui","trousse":"etui","tap":"tapis","tapi":"tapis","sur":"surround","surr":"surround","anneau":"surround","bul":"bulls","bull":"bulls","buls":"bulls","unicor":"unicorn","mision":"mission","missoin":"mission","harows":"harrows","harrow":"harrows","designas":"designa","dezigna":"designa","granboar":"granboard","granbord":"granboard","winmaux":"winmau","winmo":"winmau"};
        const fixed = fixes[q]; if (fixed) set.add(fixed);
        const dedupe = q.replace(/(.)\1+/g,"$1");
        if (dedupe !== q) { set.add(dedupe); if (fixes[dedupe]) set.add(fixes[dedupe]); }
        const normalized = this._normalizeQuery(q);
        if (normalized !== q) set.add(normalized);
        const words = q.split(" ");
        const withoutColor = words.filter(w => !this._colorMap[w]).join(" ").trim();
        if (withoutColor && withoutColor !== q && withoutColor.length > 1) set.add(withoutColor);
        return [...set].filter(v => v.length >= 2);
      },

      _matchesQuery(p, query) {
        const t = this._norm(p.title || "");
        const v = this._norm(p.vendor || "");
        const tags = (p.tags||[]).map(tag => this._norm(tag)).join(" ");
        const hay = t + " " + v + " " + tags;
        const variants = this._fuzzyStrict(query);
        return variants.some(variant => {
          const words = variant.split(" ").filter(Boolean);
          return words.length > 0 && words.every(w => this._hasWordPrefix(hay, w));
        });
      },

      async _fetchProducts(query) {
        // « débutant » : on renvoie la sélection débutant de la boutique
        if (/\bdebut/.test(this._norm(query))) {
          const list = await this._fetchJsonPages("/collections/flechettes-debutant/products.json?sort_by=manual", "id,handle,title,vendor,product_type,tags,variants,images");
          if (list.length) return list.map((p, i) => this._normalizeProduct(p, i));
        }
        const catalog = await this._loadCatalog();
        const cat = this._cat(query);
        const types = cat ? this._CAT[cat] : null;
        const modifiers = this._extractModifiers(query);

        let candidates;
        if (types) {
          candidates = catalog.filter(p => types.includes(p.type));
          if (candidates.length === 0) { candidates = catalog.filter(p => this._matchesQuery(p, query)); }
        } else {
          candidates = catalog.filter(p => this._matchesQuery(p, query));
        }

        if (modifiers.length) {
          const allMatch = candidates.filter(p => { const t = this._norm(p.title||""); return modifiers.every(m => this._hasWordPrefix(t, m)); });
          if (allMatch.length > 0) { candidates = allMatch; }
          else {
            const anyMatch = candidates.filter(p => { const t = this._norm(p.title||""); return modifiers.some(m => this._hasWordPrefix(t, m)); });
            if (anyMatch.length > 0) candidates = anyMatch;
          }
        }

        return candidates;
      },

      _score(p, query) {
        const q = this._norm(query);
        const t = this._norm(p.title||"");
        const v = this._norm(p.vendor||"");
        let sc = 0;
        if (t.startsWith(q)) sc += 20;
        else if (t.includes(q)) sc += 10;
        else if (t.split(" ").some(w => w.startsWith(q))) sc += 5;
        if (v === q || v.includes(q)) sc += 8;
        const modifiers = this._extractModifiers(query);
        modifiers.forEach(m => { if (this._hasWordPrefix(t, m)) sc += 15; });
        if (t.includes("22g")||t.includes("23g")||t.includes("24g")) sc += 2;
        return sc;
      },

      _typePriority(type) {
        const P = { "Steel Tip":1,"Soft Tip":1,"Cibles Sisal":2,"Cibles électroniques":2,"Cabinet":2,"Tiges":3,"Ailettes":3,"Fusion Ailettes Tiges":3,"Pointes acier":4,"Pointes plastiques":4,"Surround":5,"Eclairage":5,"Tapis":5,"Pack":5,"Etuis":9,"Entretien":9,"Accessoires":9 };
        return P[type] || 6;
      },

      async _runSearch() {
        this.loading = true;
        const cat = this._cat(this.query);
        const types = cat ? this._CAT[cat] : null;
        const raw = await this._fetchProducts(this.query);
        // Mots « génériques » (catégorie, poids, couleurs) : s'il reste un autre mot (marque, modèle, joueur…),
        // la recherche est précise et on classe d'abord par pertinence du texte.
        const GENERIC = ["cible","cibles","sisal","electronique","electroniques","electro","flechette","flechettes","fleche","dart","darts","pointe","pointes","acier","soft","plastique","plastiques","tip","steel","etui","etuis","ailette","ailettes","flight","flights","plume","plumes","tige","tiges","shaft","shafts","tapis","surround","eclairage","led","pack","kit","coffret","board","dartboard","cabinet","armoire","sacoche","trousse","anneau","de","des","pour","a","la","le","les","en","et"];
        const specific = this._normalizeQuery(this.query).split(" ").filter(w => w && !GENERIC.includes(w) && !this._MODIFIER_WORDS.includes(w) && !/^\d{1,2}g?$/.test(w)).length > 0;
        const scored = raw.map((product, index) => ({ product, index, score: this._score(product, this.query) }));
        scored.sort((a, b) => {
          const typeDiff = this._typePriority(a.product.type) - this._typePriority(b.product.type);
          if (typeDiff) return typeDiff;
          if (types && !specific) {
            // Recherche de catégorie : l'ordre business (ventes, marge, stock, reco_priority...)
            // déjà calculé par le Worker prime sur notre score texte, souvent bruité
            // (beaucoup de titres commencent tous par "Fléchettes ...").
            return (a.product.rankIndex - b.product.rankIndex) || (b.score - a.score) || (a.index - b.index);
          }
          // Recherche par marque/modèle précis : la pertinence texte prime, rankIndex départage.
          return (b.score - a.score) || (a.product.rankIndex - b.product.rankIndex) || (a.index - b.index);
        });
        this.products = scored.map(item => item.product);
        this.visibleCount = this.pageSize;
        this.loading = false;
      },

      get facetVendors() {
        const counts = {};
        this.products.forEach(p => { if (p.vendor) counts[p.vendor] = (counts[p.vendor]||0) + 1; });
        return Object.keys(counts).sort((a,b) => a.localeCompare(b)).map(v => ({ value: v, count: counts[v] }));
      },
      get facetTypes() {
        const counts = {};
        this.products.forEach(p => { if (p.type) counts[p.type] = (counts[p.type]||0) + 1; });
        return Object.keys(counts).sort((a,b) => a.localeCompare(b)).map(t => ({ value: t, count: counts[t] }));
      },
      get facetColors() {
        const counts = {};
        this.products.forEach(p => (p.colors||[]).forEach(c => { counts[c] = (counts[c]||0) + 1; }));
        return Object.keys(counts).sort((a,b) => a.localeCompare(b)).map(c => ({ value: c, count: counts[c] }));
      },
      get hasActiveFilters() {
        const f = this.filters;
        return f.vendors.length > 0 || f.types.length > 0 || f.colors.length > 0 || f.inStock || f.minPrice !== "" || f.maxPrice !== "";
      },

      toggleFilter(group, value) {
        const list = this.filters[group];
        const i = list.indexOf(value);
        if (i === -1) list.push(value); else list.splice(i, 1);
        this.visibleCount = this.pageSize;
      },
      toggleInStock() {
        this.filters.inStock = !this.filters.inStock;
        this.visibleCount = this.pageSize;
      },
      onPriceChange() { this.visibleCount = this.pageSize; },
      onSortChange() { this.visibleCount = this.pageSize; },
      resetFilters() {
        this.filters = { vendors: [], types: [], colors: [], inStock: false, minPrice: "", maxPrice: "" };
        this.visibleCount = this.pageSize;
      },

      get filteredProducts() {
        const f = this.filters;
        const min = parseFloat(f.minPrice);
        const max = parseFloat(f.maxPrice);
        let list = this.products.filter(p => {
          if (f.vendors.length && !f.vendors.includes(p.vendor)) return false;
          if (f.types.length && !f.types.includes(p.type)) return false;
          if (f.colors.length && !(p.colors||[]).some(c => f.colors.includes(c))) return false;
          if (f.inStock && !p.available) return false;
          const price = parseFloat(p.price) || 0;
          if (!isNaN(min) && price < min) return false;
          if (!isNaN(max) && price > max) return false;
          return true;
        });
        if (this.sortBy === "price_asc") { list = list.slice().sort((a,b) => (parseFloat(a.price)||0) - (parseFloat(b.price)||0)); }
        else if (this.sortBy === "price_desc") { list = list.slice().sort((a,b) => (parseFloat(b.price)||0) - (parseFloat(a.price)||0)); }
        return list;
      },

      get visibleProducts() { return this.filteredProducts.slice(0, this.visibleCount); },

      loadMore() { this.visibleCount += this.pageSize; },

      cardHTML(p) {
        const pr = parseFloat(p.price||"0");
        const price = pr > 0 ? pr.toFixed(2).replace(".",",")+"\u00a0€" : "";
        const cp = parseFloat(p.compare_at_price_min||"0");
        const was = cp > pr ? cp.toFixed(2).replace(".",",")+"\u00a0€" : null;
        const img = (p.featured_image?.url||p.image) ? (p.featured_image?.url||p.image)+"&width=400" : null;
        const tags = (p.tags||[]).map(t=>(t||"").toLowerCase());
        const steel = p.type==="Steel Tip"||tags.some(t=>t.includes("steel tip"));
        const soft = p.type==="Soft Tip"||tags.some(t=>t.includes("soft tip"));
        const url = p.url||"/products/"+p.handle;
        const vid = p.variants?.[0]?.id||0;
        return `<div class="pb-c"><a href="${url}" class="pb-c-link"><div class="pb-c-img-wrap">${img?`<img src="${img}" alt="${(p.title||"").replace(/"/g,"&quot;")}" loading="lazy" class="pb-c-img">`:`<div class="pb-c-img-ph"></div>`}<div class="pb-c-badges">${steel?`<span class="pb-b pb-b-steel">⚙ Steel</span>`:""}${soft?`<span class="pb-b pb-b-soft">● Soft</span>`:""}</div>${vid?`<div class="pb-c-atc"><button class="pb-c-atc-btn" onclick="event.preventDefault();event.stopPropagation();var f=document.createElement('form');f.method='POST';f.action='/cart/add';var i=document.createElement('input');i.type='hidden';i.name='id';i.value='${vid}';f.appendChild(i);document.body.appendChild(f);f.submit();" title="Ajouter au panier"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="14" height="14"><path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 01-8 0"/></svg></button></div>`:""}</div><div class="pb-c-body">${p.vendor?`<p class="pb-c-vendor">${p.vendor}</p>`:""}<h3 class="pb-c-title">${p.title||""}</h3><div class="pb-c-price-wrap"><span class="pb-c-price${was?" pb-c-price-sale":""}">${price}</span>${was?`<span class="pb-c-price-was">${was}</span>`:""}</div></div></a></div>`;
      },
    }));

    Alpine.data("xFreeShipping", (sectionId) => ({
      init() {
        document.addEventListener(`${CART_EVENT.cartUpdate}`, (e) => {
          fetch(`${window.location.pathname}?section_id=${sectionId}`)
            .then((response) => response.text())
            .then((response) => {
              const html = new DOMParser().parseFromString(response, "text/html");
              const newContent = html.getElementById("announcement-free-shipping");
              this.$el.innerHTML = newContent.innerHTML;
            });
        });
      },
    }));

    Alpine.data("xCollapsibleTab", (initIsOpen) => ({
      isOpenCollapsible: initIsOpen,
      init() {
        if (!initIsOpen) {
          let collapsibleContentEle = this.$el.querySelector(`.collapsible-tab__content`);
          if (collapsibleContentEle) {
            collapsibleContentEle.classList.remove(`hidden`);
          }
        }
      },
      handleToggleTab: function () {
        this.isOpenCollapsible = !this.isOpenCollapsible;
      }
    }));

    Alpine.data('xMediaModel', () => ({
      MAX_RETRIES_WAIT_SCROLL_END: 50,
      loaded: false,
      handleInit() {
        this.loaded = true;
        window.Shopify.loadFeatures([
          {
            name: 'shopify-xr',
            version: '1.0',
            onLoad: this.__setUpShopifyXR.bind(this),
          },
        ]);
      },
      handleLoadModel() {
        let container = this.$el.parentNode;
        const content = document.createElement('div');
        content.appendChild(container.querySelector('template').content.firstElementChild.cloneNode(true));

        this.$el.classList.add('hidden');
        container.appendChild(content.querySelector('model-viewer'));
        this.__loadViewerUI();
      },
      handlePauseViewer() {
        if (this.$el.modelViewerUI) this.$el.modelViewerUI.pause();
      },
      __setUpShopifyXR() {
        const setup = () => {
          document.querySelectorAll('[id^="ProductJSON-"]').forEach((modelJSON) => {
            window.ShopifyXR.addModels(JSON.parse(modelJSON.textContent));
            modelJSON.remove();
          });
          window.ShopifyXR.setupXRElements();
        }

        if (!window.ShopifyXR) {
          document.addEventListener('shopify_xr_initialized', () => {
            setup();
          });
          return;
        }

        setup();
      },
      __loadViewerUI() {
        window.Shopify.loadFeatures([
          {
            name: 'model-viewer-ui',
            version: '1.0',
            onLoad: this.__setUpViewerUI.bind(this),
          },
        ]);
      },
      __setUpViewerUI(errors) {
        if (errors) return;

        this.$el.parentNode.modelViewerUI
          = new Shopify.ModelViewerUI(this.$el.parentNode.querySelector('model-viewer'));
      },
      waitScrollEndedOnDesktop(callback) {
        let count = 0;
        const isMobile = window.innerWidth < MIN_DEVICE_WIDTH.tablet ? true : false;
        const galleryWrapperDataset = this.$el.closest('.media-gallery__wrapper')?.dataset;

        const intervalId = setInterval(() => {
          if (
            isMobile ||
            galleryWrapperDataset?.isScrollingOnDesktop != 'true' ||
            count >= this.MAX_RETRIES_WAIT_SCROLL_END
          ) {
            if (galleryWrapperDataset) {
              galleryWrapperDataset.isScrollingOnDesktop = 'false';
            }
            clearInterval(intervalId);
            callback();
            return;
          }

          count++;
        }, TIMEOUT.scrollIdle);
      },
      initModelInSlideEffect() {
        this.waitScrollEndedOnDesktop(() => {
          const mediaitem = this.$el.closest('.media-gallery__media-item');
          if (this.loaded || !mediaitem.classList.contains('is-active-in-two-columns')) return;
          this.handleInit();
          this.handleLoadModel();
        });

        document.addEventListener(`${PRODUCT_MEDIA_EVENT.show_model}-${this.$el.dataset.mediaId}`, (event) => { 
          if (this.loaded) return;
          if (window.innerWidth >= MIN_DEVICE_WIDTH.tablet) {
            this.waitScrollEndedOnDesktop(() => {
              if (this.loaded) return;
              this.handleInit();
              this.handleLoadModel();
            })
          } else {
            this.handleInit();
            this.handleLoadModel();
          }
        });
      }
    }))
  });
});

requestAnimationFrame(() => {
  document.addEventListener('alpine:init', () => {
    Alpine.store('xCustomerEvent', {
      fire(eventName, el, data) {
        if (Shopify.designMode) return;

        const formatedData = data ? data : xParseJSON(el.dataset.customEvent);
        Shopify.analytics.publish(eventName, formatedData);
      }
    });

    Alpine.store("xEstimateDelivery", {
      hour: 0,
      minute: 0,
      notification: '',
      countdownCutOffTime(cutOffTime, hrsText, minusText) {
        if (this.notification != '')
          return;
        const now = new Date();
        const currentHour = now.getHours();
        const currentMinute = now.getMinutes();

        const cutOffHour = cutOffTime.length >= 3 ? parseInt(cutOffTime.slice(0, -2), 10) : 0;
        const cutOffMinute = cutOffTime.length >= 3 ? parseInt(cutOffTime.slice(-2), 10) : 0;

        const current = new Date(now.getFullYear(), now.getMonth(), now.getDate(), currentHour, currentMinute);
        const cutOff = new Date(now.getFullYear(), now.getMonth(), now.getDate(), cutOffHour, cutOffMinute);

        if (current >= cutOff) {
          cutOff.setDate(cutOff.getDate() + 1);
        }

        const diffMs = cutOff - current;

        this.hour = Math.floor(diffMs / 1000 / 60 / 60);
        this.minute = Math.floor((diffMs / 1000 / 60) % 60);
        return this.hour > 0 ? this.notification = this.hour + ' ' + hrsText + ' ' + this.minute + ' ' + minusText : this.notification = this.minute + ' ' + minusText;
      }
    });

  });
});

requestAnimationFrame(() => {
  document.addEventListener("alpine:init", () => {
    Alpine.store("xScrollPromotion", {
      load(el) {
        let scroll = el.getElementsByClassName("el_animate");
        for (let i = 0; i < scroll.length; i++) {
          scroll[i].classList.add("animate-scroll-banner");
        }
      },
    });
  });
});

requestAnimationFrame(() => {
  document.addEventListener('alpine:init', () => {
    Alpine.store('xResizeHandler', {
      callbacks: [],
      triggerResize: null,
      debounce(func, delay) {
        let timer;
        return function (...args) {
          clearTimeout(timer);
          timer = setTimeout(() => func.apply(this, args), delay);
        };
      },
      register(callback, allowDuplicateFuntc, ...params) {
        if (allowDuplicateFuntc) {
          this.callbacks.push({callback, params});
        } else if (!this.callbacks.find(item => item.callback === callback)) {
          this.callbacks.push({callback, params});
        }
      },
      unregister(callback) {
        this.callbacks = this.callbacks.filter(item => item.callback !== callback);
      },
      init() {
        this.triggerResize = this.debounce(() => {
          this.callbacks.forEach(({callback, params}) => {
            callback(...params);
          });
        }, 50);

        window.addEventListener("resize", this.triggerResize);
      }
    });
  });
});

function maximizeParseJSON(jsonString) {
  jsonString = String.raw`${jsonString}`;
  jsonString = jsonString.replaceAll("\\", "\\\\").replaceAll('\\"', '\"');

  return JSON.parse(jsonString);
}

requestAnimationFrame(() => {
    document.addEventListener('alpine:init', () => {
    Alpine.data('xProductDrawer', () => ({
      isShowDrawer: false,
      openDrawer(idBlockDrawer) {
        this.isShowDrawer = true;
        Alpine.store('xMaximizeDrawer').handleOpen(idBlockDrawer);
      },
      closeDrawer(closeQuickView = false) {
        Alpine.store('xFocusElement').removeTrapFocus();
        this.isShowDrawer = false;
        let closeAllDrawer = true;
        const isShowQuickView = Alpine.store('xQuickView') && Alpine.store('xQuickView').show
        if (isShowQuickView && (!closeQuickView || window.innerWidth < MIN_DEVICE_WIDTH.tablet)) {
          closeAllDrawer = false
        }
        Alpine.store('xMaximizeDrawer').handleClose(closeAllDrawer);
        if (isShowQuickView && closeQuickView && window.innerWidth >= MIN_DEVICE_WIDTH.tablet) {
          Alpine.store('xQuickView').close();
        }
      }
    }));
  })
});
// xProductComparison extracted to product-comparison.js

requestAnimationFrame(() => {
  document.addEventListener("alpine:init", () => {
    Alpine.store("xStoreMap", {
      currentStoreSelected: JSON.parse(localStorage.getItem("currentStoreSelected"))?.selectedStore || "",

      getStore() {
        if (this.currentStoreSelected) {
          return this.currentStoreSelected.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;").replace(/'/g, "&#39;");
        }
      },
      getStoreText() {
        if (this.currentStoreSelected !== "") {
          return this.currentStoreSelected;
        }
      },
      checkAvailability(storeAvailabilities) {
        let store = this.getStore();
        if (storeAvailabilities) {
          return storeAvailabilities[store];
        }
      },
      dispatchEventOpenPopup() {
        document.dispatchEvent(new CustomEvent(`${STORE_MAP_EVENT.openPopup}`));
      }
    });
  })
})

requestAnimationFrame(() => {
  document.addEventListener("alpine:init", () => {
    Alpine.data('xContactForm', (formId) => ({
      isFormValid: false,
      formId: formId,
      emailTouched: false,
      get form() {
        return this.$el ? this.$el.querySelector(`#${this.formId}`) : null;
      },

      get emailInput() {
        return this.form ? this.form.querySelector('input[name="contact[email]"]') : null;
      },

      get emailValidationMessage() {
        return this.form ? this.form.querySelector(`.contact-form__email-error-${this.formId}`) : null;
      },

      init() {
        if (!this.form) return;
        this.validateForm();
        if (this.emailInput) {
          this.emailInput.addEventListener('input', () => {
            this.emailTouched = true;
            this.validateForm();
          });
        }
        
        this.$el.addEventListener(SELECT_ELEMENT_EVENT.change, () => {
          this.validateForm();
        });

        this.initRequestTypeNote();
      },

      validateForm() {
        const form = this.form;
        if (!form) return;

        let isValid = true;

        const requiredFields = form.querySelectorAll('input[required], textarea[required]');
        requiredFields.forEach(field => {
          if (field.classList.contains('input-checkbox')) return;
          if (field.classList.contains('content') && field.closest('.multi-select')) return;
          if (!field.value || !field.value.trim()) isValid = false;
        });

        if (this.emailInput) {
          const isEmpty = !this.emailInput.value.trim();
          const isRequired = this.emailInput.hasAttribute('required');
          const validation = validateEmail(this.emailInput.value);

          const isRequiredError = isEmpty && isRequired;
          const isFormatError = validation.isInvalid;
          const hasError = isRequiredError || isFormatError || validation.isError;

          const errorMessage = isRequiredError 
            ? window.Maximize?.formTranslations?.emailRequiredMessage 
            : (isFormatError ? window.Maximize?.formTranslations?.emailInvalidMessage  : '');

          if (hasError) isValid = false;
          this.showMessage(this.emailTouched && hasError, this.emailValidationMessage, errorMessage);
        } else {
          this.isFormValid = false;
          return;
        }

        const checkboxGroups = form.querySelectorAll('.multi-select');
        checkboxGroups.forEach(group => {
          const contentInput = group.querySelector('.content');
          const isRequired = contentInput && contentInput.hasAttribute('required');
          if (!isRequired) return;

          const checkedCount = group.querySelectorAll('.input-checkbox:checked').length;
          if (checkedCount === 0) isValid = false;
        });

        this.isFormValid = isValid;
      },

      showMessage(isShow, element, message) {
        if (!element) return;
        if (isShow) {
          element.textContent = message;
          element.classList.remove('hidden');
        }else {
          element.classList.add('hidden');
        }
      },

      initRequestTypeNote() {
        const requestTypeRadios = this.form.querySelectorAll('.request-type-radio');
        const noteSpan = this.form.querySelector('.request-type_note');

        if (!requestTypeRadios.length || !noteSpan) return;

        requestTypeRadios.forEach(radio => {
          radio.addEventListener('change', () => this.updateNoteRequestType());
        });

        this.updateNoteRequestType();
      },
      updateNoteRequestType() {
        const noteSpan = this.form.querySelector('.request-type_note');
        if (!noteSpan) return;

        const selected = this.form.querySelector('.request-type-radio:checked');
        if (!selected) return;

        const text = selected.value === 'Return' 
          ? noteSpan.dataset.returnText 
          : noteSpan.dataset.cancelText;

        noteSpan.textContent = text || '';
      },
    }));
  });
})

requestAnimationFrame(() => {
  document.addEventListener("alpine:init", () => {
    Alpine.data('xDisclosures', () => ({
      isOpen: false,
      isOpenQuickView: false, 
      isOpenCartDrawer: false, 
      openDrawer(drawerId) {
        this.isOpen = true;
        this.$store.xMaximizeDrawer.handleOpen(`#DisclosuresDrawer__${drawerId}`);
        const cartDrawer = document.getElementById('CartDrawer');
        if (Alpine.store('xQuickView') && Alpine.store('xQuickView').show === true) {
          Alpine.store('xQuickView').close(); 
          this.isOpenQuickView = true;
        }
        if (cartDrawer && cartDrawer.isOpen) {
          this.isOpenCartDrawer = true
          cartDrawer.isOpen = false;
        }
        Alpine.nextTick(() => {
          Alpine.store('xFocusElement').trapFocus(`DisclosuresDrawer__${drawerId}`, `DisclosureDrawerClose__${drawerId}`);
        });
      },

      closeDrawer(productId, productUrl) {
        this.isOpen = false;
        this.$store.xMaximizeDrawer.handleClose();
        const cartDrawer = document.getElementById('CartDrawer');
        if (this.isOpenCartDrawer && !this.isOpenQuickView && cartDrawer) {
          cartDrawer.isOpen = true;
          this.isOpenCartDrawer = false
        }
        if (Alpine.store('xQuickView') && this.isOpenQuickView && productId && productUrl) {
          Alpine.store('xQuickView').load(productId, productUrl, this.$el);
          Alpine.store('xQuickView').open();
          this.isOpenQuickView = false;
        }
        Alpine.store('xFocusElement').removeTrapFocus();
      }
    }));
  });
});

function formatWithDelimiters(number, precision, thousands, decimal) {
  precision = defaultOption(precision, 2);
  thousands = defaultOption(thousands, ',');
  decimal = defaultOption(decimal, '.');

  if (isNaN(number) || number == null) {
    return 0;
  }

  number = (number / 100.0).toFixed(precision);

  let parts = number.split('.'),
    dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands),
    cents = parts[1] ? (decimal + parts[1]) : '';

  return dollars + cents;
}

function defaultOption(opt, def) {
  return (typeof opt == 'undefined' ? def : opt);
}

function formatMoney(amount, formatString) {
  let placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
  let value;
  switch (formatString.match(placeholderRegex)[1]) {
    case 'amount':
      value = formatWithDelimiters(amount, 2);
      break;
    case 'amount_no_decimals':
      value = formatWithDelimiters(amount, 0);
      break;
    case 'amount_with_comma_separator':
      value = formatWithDelimiters(amount, 2, '.', ',');
      break;
    case 'amount_no_decimals_with_comma_separator':
      value = formatWithDelimiters(amount, 0, '.', ',');
      break;
  }

  return formatString.replace(placeholderRegex, value);
}
window.formatMoney = formatMoney;

function debounce(func, wait) {
  let timeout;
  return function (...args) {
    clearTimeout(timeout);
    timeout = setTimeout(() => func.apply(this, args), wait);
  };
}

function validateEmail(email) {
  const VALID_REGEX_EMAIL = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)+$/;
  if (email === '') {
    return {'isError': true, 'isRequired': true};
  } else if (!email.match(VALID_REGEX_EMAIL)) {
    return {'isError': true, 'isInvalid': true};
  } else {
    return {'isError': false};
  }
}


function getValueCssVariable(varName, element = document.documentElement, shouldConvertToFloat = false) {
  const value = getComputedStyle(element)
    .getPropertyValue(varName)
    .trim();

  if (!shouldConvertToFloat) return value;
    
  if (!value) return 0;
  const rootFontSize = parseFloat(
    getComputedStyle(document.documentElement).fontSize
  );

  const elementFontSize = parseFloat(
    getComputedStyle(element).fontSize
  );

  const numericValue = parseFloat(value);

  if (isNaN(numericValue)) return 0;

  if (value.endsWith('ms')) {
    return numericValue;
  }

  if (value.endsWith('s')) {
    return numericValue * MS_PER_SECOND;
  }

  if (value.endsWith('rem')) {
    return numericValue * rootFontSize;
  }

  if (value.endsWith('em')) {
    return numericValue * elementFontSize;
  }

  return numericValue;
}

// =============================================================================
// Highlight animation & fade-on-intersect observer
// Replaces x-data + x-intersect Alpine directives across all liquid files.
// A single shared IntersectionObserver handles all elements — no Alpine overhead.
//
//   data-highlight-class="heading-highlight-underline--active"
//       → adds that class after optional delay (default: immediate)
//   data-highlight-delay="700"
//       → delay in ms before adding the highlight class
//   data-fade-on-intersect
//       → adds 'is-visible' class (triggers CSS fade-in animation)
// =============================================================================
(function setupHighlightObserver() {
  let SELECTOR = '[data-highlight-class],[data-fade-on-intersect]';

  let observer = new IntersectionObserver(function(entries, obs) {
    entries.forEach(function(entry) {
      if (!entry.isIntersecting) return;
      let el = entry.target;
      obs.unobserve(el);

      // Highlight animation: add the target class after an optional delay
      if (el.dataset.highlightClass) {
        let delay = parseInt(el.dataset.highlightDelay, 10) || 0;
        if (delay) {
          setTimeout(function() { el.classList.add(el.dataset.highlightClass); }, delay);
        } else {
          el.classList.add(el.dataset.highlightClass);
        }
      }

      // Fade animation: equivalent to x-intersect.once="$el.classList.add('is-visible')"
      if ('fadeOnIntersect' in el.dataset) {
        el.classList.add('is-visible');
      }
    });
  }, { rootMargin: '0px 0px -20px 0px' });

  // Elements already in viewport at load time: apply is-visible immediately
  // without transition so Lighthouse screenshots count them as visible content,
  // reducing Speed Index. Elements below fold still use the normal fade-in.
  // Batch pattern: Pass 1 reads all rects (single reflow, zero DOM writes),
  // Pass 2 writes classList + dataset (single style recalculation) — avoids
  // N forced reflows caused by interleaving dataset writes with rect reads.
  function applyInViewport(root) {
    let vh = window.innerHeight;
    let toShow = [], toObserve = [];
    // Pass 1: batch-read all rects — NO dataset writes, zero DOM mutations
    (root || document).querySelectorAll(SELECTOR).forEach(function(el) {
      if (el.dataset.fadeObserved) return;
      let rect = el.getBoundingClientRect();
      if (rect.top < vh && rect.bottom > 0) {
        toShow.push(el);
      } else {
        toObserve.push(el);
      }
    });
    // Pass 2: batch-write all DOM mutations — single style recalculation
    toShow.forEach(function(el) {
      el.dataset.fadeObserved = '1';
      el.classList.add('is-visible', 'transition-none');
      if (el.dataset.highlightClass) {
        el.classList.add(el.dataset.highlightClass);
      }
    });
    toObserve.forEach(function(el) {
      el.dataset.fadeObserved = '1';
      observer.observe(el);
    });
  }

  function observeAll(root) {
    (root || document).querySelectorAll(SELECTOR).forEach(function(el) {
      if (!el.dataset.fadeObserved) {
        el.dataset.fadeObserved = '1';
        observer.observe(el);
      }
    });
  }

  // Observe all static elements (theme.js runs after DOMContentLoaded via defer)
  applyInViewport();

  // Re-scan after Alpine finishes initialising (catches elements inside x-if / x-for)
  // Also remove opacity-main-header here — fires after ALL x-teleport/x-init done,
  // much earlier than @load.window (which waits for images/fonts to finish loading).
  document.addEventListener('alpine:initialized', function() {
    observeAll();
    // handleSharedCart moved here from x-init on #MainContent — avoids Alpine
    // registering the entire MainContent subtree as a managed node, which was
    // causing unnecessary style recalculations before LCP.
    if (window.xCartHelper) window.xCartHelper.handleSharedCart();
    requestAnimationFrame(function() {
      let header = document.querySelector('.opacity-main-header');
      if (header) header.classList.remove('opacity-main-header');
    });
  });

  // Auto-observe elements injected dynamically (section rendering, lazy sections, etc.)
  // childList+subtree only fires on element add/remove — NOT on attribute/text changes,
  // so Alpine reactive bindings (x-bind, x-show…) do NOT trigger this callback.
  // firstElementChild guard skips querySelectorAll on leaf nodes (button, span, img…),
  // keeping per-mutation cost near-zero when no fade elements are present.
  new MutationObserver(function(mutations) {
    for (let i = 0; i < mutations.length; i++) {
      let added = mutations[i].addedNodes;
      for (let j = 0; j < added.length; j++) {
        let node = added[j];
        if (node.nodeType !== 1) continue;
        if (node.matches(SELECTOR) && !node.dataset.fadeObserved) {
          node.dataset.fadeObserved = '1';
          observer.observe(node);
        }
        if (node.firstElementChild) {
          node.querySelectorAll(SELECTOR).forEach(function(el) {
            if (!el.dataset.fadeObserved) {
              el.dataset.fadeObserved = '1';
              observer.observe(el);
            }
          });
        }
      }
    }
  }).observe(document.body, { childList: true, subtree: true });

  // Expose so lazy Alpine.initTree() sections can register newly-revealed elements
  window._fadeObserver = { observe: observeAll };
})();


// === Pickup Message — pure JS replacement for Alpine x-data/x-show ===
// Replaces 1 Alpine component + 3 x-show directives per product card.
// Data is stored as pipe-delimited strings in data-pickup-locs / data-pickup-avail.
// A single event listener handles all cards instead of N reactive Alpine scopes.
(function setupPickupMessage() {
  // Only run on pages that actually render pickup-message.liquid snippets
  if (!document.querySelector('.pickup-message__wrapper')) return;

  // Parse "Name A|||Name B|||" → ['Name A', 'Name B']
  function parsePipeList(str) {
    return (str || '').split('|||').map(function(s) { return s.trim(); }).filter(Boolean);
  }

  // Parse "Name A::true|||Name B::false|||" → { 'Name A': true, 'Name B': false }
  function parseAvailMap(str) {
    let map = {};
    parsePipeList(str).forEach(function(entry) {
      let idx = entry.lastIndexOf('::');
      if (idx === -1) return;
      map[entry.slice(0, idx)] = entry.slice(idx + 2) === 'true';
    });
    return map;
  }

  function applyStore(storeName) {
    document.querySelectorAll('.pickup-message__wrapper[data-pickup-locs]').forEach(function(el) {
      if (!storeName) { el.style.display = 'none'; return; }

      let locs = parsePipeList(el.dataset.pickupLocs);
      if (!locs.includes(storeName)) { el.style.display = 'none'; return; }

      let avail = parseAvailMap(el.dataset.pickupAvail);
      let isAvail = avail[storeName] === true;

      el.style.display = '';
      el.querySelector('.pickup-msg-avail').style.display  = isAvail ? '' : 'none';
      el.querySelector('.pickup-msg-unavail').style.display = isAvail ? 'none' : '';
      el.querySelectorAll('.pickup-store-name').forEach(function(s) { s.textContent = storeName; });
    });
  }

  // Init from localStorage (store may already be selected from previous visit)
  let saved = JSON.parse(localStorage.getItem('currentStoreSelected') || 'null');
  if (saved && saved.selectedStore) applyStore(saved.selectedStore);

  // React to store selection / deselection
  document.addEventListener('maximize:store-changed', function(e) {
    applyStore(e.detail.store || '');
  });
})();

// Global helper: replace innerHTML of elements matching `selector` inside `rootEl`
// with the corresponding elements from a fetched/parsed `dataHTML` document.
// Matches by index; if the new document has a single match it is applied to all.
function updateElements(rootEl, dataHTML, selector) {
  const oldElements = rootEl.querySelectorAll(selector);
  const newElements = dataHTML.querySelectorAll(selector);
  const single = newElements.length === 1;

  oldElements.forEach((oldEl, i) => {
    const newEl = single ? newElements[0] : newElements[i];
    if (oldEl && newEl) oldEl.innerHTML = newEl.innerHTML;
  });
}
